import { Routes, Route, Link, useNavigate, useSearchParams, NavLink, Navigate, Outlet, useLocation } from "react-router";
import { useEffect, useState, useCallback } from "react";
import { LegalPrivacy, LegalTerms, LegalSubprocessors, LegalDPA, SecurityPage } from "./legal-pages";

// ─────────────────────────────────────────────────────────────────────
// Host topology
//
// Marketing (Landing + /legal + /security) lives on the apex
// deadsimpleagent.com. The platform (sign-in + /app + the API it calls)
// lives on app.deadsimpleagent.com, which is gated by Cloudflare Access.
// These helpers route users across the boundary cleanly.
// ─────────────────────────────────────────────────────────────────────

const APP_HOST = "app.deadsimpleagent.com";
const MARKETING_HOST = "deadsimpleagent.com";

function onMarketingHost(): boolean {
  return typeof window !== "undefined" && window.location.hostname === MARKETING_HOST;
}

/** Href for a platform path — cross-host absolute when on the marketing apex,
 *  internal otherwise (app host or localhost dev). */
function platformHref(path: string): string {
  return onMarketingHost() ? `https://${APP_HOST}${path}` : path;
}

/** Redirect apex visitors who land on a platform path over to the app host. */
function RequireAppHost({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  if (onMarketingHost()) {
    window.location.replace(`https://${APP_HOST}${location.pathname}${location.search}`);
    return <FullPageLoader label="Redirecting to the secure app…" />;
  }
  return <>{children}</>;
}

export function App() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/sign-in" element={<RequireAppHost><SignIn /></RequireAppHost>} />
      <Route path="/sign-in/sent" element={<RequireAppHost><SignInSent /></RequireAppHost>} />
      <Route path="/security" element={<SecurityPage />} />
      <Route path="/legal/privacy" element={<LegalPrivacy />} />
      <Route path="/legal/terms" element={<LegalTerms />} />
      <Route path="/legal/subprocessors" element={<LegalSubprocessors />} />
      <Route path="/legal/dpa" element={<LegalDPA />} />
      <Route path="/app" element={<RequireAppHost><AppShell /></RequireAppHost>}>
        <Route index element={<NOC />} />
        <Route path="overview" element={<Overview />} />
        <Route path="integrations" element={<Integrations />} />
        <Route path="skills" element={<Skills />} />
        <Route path="try-alan" element={<TryAlan />} />
        <Route path="sentiment" element={<SentimentPage />} />
        <Route path="qa" element={<QaPage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="team" element={<TeamPage />} />
        <Route path="alerts" element={<AlertsPage />} />
        <Route path="security" element={<SecurityCentre />} />
        <Route path="settings" element={<Settings />} />
        <Route path="audit" element={<Audit />} />
        <Route path="onboarding" element={<Navigate to="/app/integrations" replace />} />
      </Route>
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Marketing landing (kept from previous version)
// ─────────────────────────────────────────────────────────────────────

function Landing() {
  return (
    <>
      <TopBar />
      <Hero />
      <Lifecycle />
      <TriageSample />
      <SkillsMarketing />
      <Stack />
      <Safety />
      <Pricing />
      <FAQ />
      <FinalCTA />
      <Footer />
    </>
  );
}

function TriageSample() {
  // Real-looking output from services/triage.ts → buildSystemPrompt() → Haiku 4.5
  // tool_use → Priority Engine. The JSON shown matches the TriageDecision shape.
  return (
    <section id="sample" style={{ padding: "4rem 0 5rem", borderTop: "1px solid var(--line)", background: "var(--bg-1)" }}>
      <div style={{ maxWidth: 1180, margin: "0 auto", padding: "0 2rem" }}>
        <p className="eyebrow-accent" style={{ marginBottom: "0.6rem" }}>Anatomy of a triage</p>
        <h2 style={{ fontFamily: "var(--font-display)", fontSize: "clamp(2rem, 4vw, 3rem)", fontWeight: 700, letterSpacing: "-0.02em", marginBottom: "1rem", lineHeight: 1.08, maxWidth: "26ch" }}>
          What Alan actually produces, per ticket.
        </h2>
        <p style={{ color: "var(--ink-1)", maxWidth: "60ch", marginBottom: "2.5rem", lineHeight: 1.6, fontSize: "0.98rem" }}>
          Below is a single triage decision returned by Claude Haiku 4.5 against a real-shape password-reset ticket, post Priority Engine modulation. Every field is structured, deterministic, and audit-logged. No prose, no improvisation.
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "minmax(260px, 1fr) minmax(0, 1.4fr)", gap: "1.25rem", alignItems: "stretch" }}>
          <div style={{ padding: "1.4rem 1.6rem", border: "1px solid var(--line)", background: "var(--bg-0)", borderRadius: "var(--r-md)" }}>
            <div className="mono" style={{ color: "var(--ink-2)", fontSize: "0.68rem", letterSpacing: "0.1em", marginBottom: "0.85rem" }}>INPUT · HALOPSA TICKET #48211</div>
            <div style={{ fontSize: "0.8rem", color: "var(--ink-2)", marginBottom: "0.35rem" }}>From: jane.smith@mountview-estates.co.uk</div>
            <div style={{ fontSize: "0.8rem", color: "var(--ink-2)", marginBottom: "0.95rem" }}>Customer: Mountview Estates (SLA: premium)</div>
            <div style={{ color: "var(--ink-0)", fontSize: "0.92rem", lineHeight: 1.55, fontWeight: 500, marginBottom: "0.55rem" }}>Subject: Locked out, urgent</div>
            <div style={{ color: "var(--ink-1)", fontSize: "0.88rem", lineHeight: 1.6, fontStyle: "italic", borderLeft: "2px solid var(--line-2)", paddingLeft: "0.85rem" }}>
              Hi — I tried to log in this morning and my password isn't working. I'm meant to be in a client meeting at 10. Can someone reset it? I'm on my laptop. Thanks. Jane
            </div>
          </div>
          <div style={{ padding: "1.4rem 1.6rem", border: "1px solid var(--accent-line)", background: "linear-gradient(180deg, var(--bg-1), var(--bg-0))", borderRadius: "var(--r-md)", fontFamily: "var(--font-mono)", fontSize: "0.78rem", lineHeight: 1.65, color: "var(--ink-1)" }}>
            <div style={{ color: "var(--accent)", fontSize: "0.68rem", letterSpacing: "0.1em", marginBottom: "0.85rem" }}>OUTPUT · triage_decision tool_use</div>
            <div style={{ whiteSpace: "pre-wrap" }}>
              <span style={{ color: "var(--ink-2)" }}>{"{"}</span>{"\n"}
              {"  "}<span style={{ color: "var(--violet)" }}>"skill"</span>: <span style={{ color: "var(--ok)" }}>"password-reset"</span>,{"\n"}
              {"  "}<span style={{ color: "var(--violet)" }}>"confidence"</span>: <span style={{ color: "var(--info)" }}>92</span>,{"\n"}
              {"  "}<span style={{ color: "var(--violet)" }}>"priority"</span>: <span style={{ color: "var(--ok)" }}>"high"</span>,{"\n"}
              {"  "}<span style={{ color: "var(--violet)" }}>"priority_numeric"</span>: <span style={{ color: "var(--info)" }}>2</span>,{"\n"}
              {"  "}<span style={{ color: "var(--violet)" }}>"impact_score"</span>: <span style={{ color: "var(--info)" }}>3</span>,{"\n"}
              {"  "}<span style={{ color: "var(--violet)" }}>"urgency_score"</span>: <span style={{ color: "var(--info)" }}>4</span>,{"\n"}
              {"  "}<span style={{ color: "var(--violet)" }}>"category"</span>: <span style={{ color: "var(--ok)" }}>"Account Administration{">"}Password"</span>,{"\n"}
              {"  "}<span style={{ color: "var(--violet)" }}>"customer_impact"</span>: <span style={{ color: "var(--ok)" }}>"Single user blocked from work,{"\n"}                       client meeting in 30 min."</span>,{"\n"}
              {"  "}<span style={{ color: "var(--violet)" }}>"recommended_action"</span>: <span style={{ color: "var(--ok)" }}>"auto_route_ai"</span>,{"\n"}
              {"  "}<span style={{ color: "var(--violet)" }}>"priority_trace"</span>: [{"\n"}
              {"    "}<span style={{ color: "var(--ok)" }}>"base=I3×U4 → P3"</span>,{"\n"}
              {"    "}<span style={{ color: "var(--ok)" }}>"premium SLA + revenue-blocking{"\n"}     meeting — promoted to P2"</span>{"\n"}
              {"  "}],{"\n"}
              {"  "}<span style={{ color: "var(--violet)" }}>"tier_used"</span>: <span style={{ color: "var(--ok)" }}>"haiku"</span>,{"\n"}
              {"  "}<span style={{ color: "var(--violet)" }}>"model"</span>: <span style={{ color: "var(--ok)" }}>"claude-haiku-4-5"</span>,{"\n"}
              {"  "}<span style={{ color: "var(--violet)" }}>"tokens_in"</span>: <span style={{ color: "var(--info)" }}>4127</span>,{"\n"}
              {"  "}<span style={{ color: "var(--violet)" }}>"tokens_out"</span>: <span style={{ color: "var(--info)" }}>184</span>{"\n"}
              <span style={{ color: "var(--ink-2)" }}>{"}"}</span>
            </div>
          </div>
        </div>
        <p className="mono" style={{ marginTop: "1.5rem", color: "var(--ink-2)", fontSize: "0.75rem", textAlign: "center" }}>
          Round-trip latency: <span style={{ color: "var(--ink-0)" }}>~840ms</span> · Cost: <span style={{ color: "var(--ink-0)" }}>£0.0021</span> · Cache hit on identical re-fire: <span style={{ color: "var(--ink-0)" }}>~10ms / £0</span>
        </p>
      </div>
    </section>
  );
}

function TopBar() {
  return (
    <header style={{ position: "sticky", top: 0, zIndex: 50, backdropFilter: "blur(14px)", background: "rgba(10,10,11,0.78)", borderBottom: "1px solid var(--line)" }}>
      <div style={{ maxWidth: 1180, margin: "0 auto", padding: "1rem 2rem", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: "0.85rem" }}>
          <span style={{ fontFamily: "var(--font-display)", fontSize: "1.5rem", fontWeight: 700, letterSpacing: "-0.02em" }}>
            Alan<span style={{ color: "var(--accent)" }}>.</span>
          </span>
          <span className="eyebrow">by Dead Simple Computing</span>
        </div>
        <nav style={{ display: "flex", gap: "1.75rem", alignItems: "center" }}>
          <a href="#how" className="mono" style={{ fontSize: "0.85rem", color: "var(--ink-1)" }}>How it works</a>
          <a href="#skills" className="mono" style={{ fontSize: "0.85rem", color: "var(--ink-1)" }}>Skills</a>
          <a href="#safety" className="mono" style={{ fontSize: "0.85rem", color: "var(--ink-1)" }}>Safety</a>
          <a href="#pricing" className="mono" style={{ fontSize: "0.85rem", color: "var(--ink-1)" }}>Pricing</a>
          <a href={platformHref("/sign-in")} style={{ padding: "0.6rem 1.1rem", background: "var(--accent)", color: "var(--accent-ink)", borderRadius: "var(--r-sm)", fontWeight: 700, fontSize: "0.9rem" }}>Sign in →</a>
        </nav>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section style={{ position: "relative", overflow: "hidden", padding: "5rem 0 5rem" }}>
      <div style={{ maxWidth: 1180, margin: "0 auto", padding: "0 2rem", position: "relative", zIndex: 2 }}>
        <h1 style={{ fontFamily: "var(--font-display)", fontSize: "clamp(2.6rem, 6vw, 4.6rem)", fontWeight: 700, lineHeight: 1.02, letterSpacing: "-0.025em", maxWidth: "22ch", marginBottom: "1.5rem" }}>
          The tier-1 service desk engineer your team would hire if it could find one.
        </h1>
        <p style={{ fontSize: "1.15rem", color: "var(--ink-1)", maxWidth: "60ch", lineHeight: 1.6, marginBottom: "2.5rem" }}>
          Alan is an autonomous AI agent that reads, triages, and resolves <b style={{ color: "var(--ink-0)" }}>HaloPSA</b> tickets — password resets, M365 onboarding, MFA, mailbox config, VPN, license audits, alert investigation. Every action gates through your engineers until you choose to let it run unsupervised.
        </p>
        <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
          <a href={platformHref("/sign-in")} style={{ padding: "0.85rem 1.4rem", background: "var(--accent)", color: "var(--accent-ink)", borderRadius: "var(--r-sm)", fontWeight: 700 }}>Sign in / start free pilot →</a>
          <a href="#how" style={{ padding: "0.85rem 1.4rem", border: "1px solid var(--line-2)", borderRadius: "var(--r-sm)", color: "var(--ink-0)" }}>See it on a real ticket</a>
        </div>
      </div>
    </section>
  );
}

function Lifecycle() {
  return (
    <section id="how" style={{ padding: "4rem 0 5rem", borderTop: "1px solid var(--line)" }}>
      <div style={{ maxWidth: 1180, margin: "0 auto", padding: "0 2rem" }}>
        <p className="eyebrow-accent" style={{ marginBottom: "0.6rem" }}>How it works</p>
        <h2 style={{ fontFamily: "var(--font-display)", fontSize: "clamp(2rem, 4vw, 3rem)", fontWeight: 700, letterSpacing: "-0.02em", maxWidth: "22ch", marginBottom: "2.5rem", lineHeight: 1.08 }}>
          One ticket, four states. Engineers gate every transition.
        </h2>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "1rem" }}>
          <TicketCard id="#48211" customer="Mountview Estates" summary={'"Hi — can\'t get on the VPN since this morning, says credentials wrong."'} statusLabel="With AI Agent" statusColor="var(--info)" statusBg="var(--info-soft)" note='Engineer clicked "Send to AI" · queued at 09:14:22' />
          <TicketCard id="#48211" customer="analysis complete" summary="Identified VPN auth issue. Likely AD password change last night flushed cached creds." statusLabel="AI Proposal Ready" statusColor="var(--violet)" statusBg="var(--violet-soft)" note="Pulled NinjaRMM device last-logged-in user, matched against 3 similar tickets." buttons={["Approve", "Feedback", "Reject"]} />
          <TicketCard id="#48211" customer="drafting reply" summary="Drafted email to user with reset steps. Awaiting send approval — no email leaves without you." statusLabel="AI Email Draft" statusColor="var(--warn)" statusBg="var(--warn-soft)" note='Tone matches house style. Signed "Support Team". Calendly link included.' buttons={["Approve send", "Edit draft"]} />
          <TicketCard id="#48211" customer="resolved" summary='User confirmed back on. Runbook for "VPN cred refresh" auto-generated for Mountview.' statusLabel="Closed" statusColor="var(--ok)" statusBg="var(--ok-soft)" note="Total engineer time: ~45 seconds across two clicks. Alan did the rest." />
        </div>
        <p className="mono" style={{ textAlign: "center", marginTop: "1.5rem", color: "var(--ink-2)", fontSize: "0.75rem", letterSpacing: "0.04em" }}>
          Engineer clicks → Alan reads context → Alan proposes → Engineer approves → Alan executes ·{" "}
          <strong style={{ color: "var(--ink-0)" }}>every transition logged, every send gated</strong>
        </p>
      </div>
    </section>
  );
}

function TicketCard(props: { id: string; customer: string; summary: string; statusLabel: string; statusColor: string; statusBg: string; note: string; buttons?: string[] }) {
  return (
    <div style={{ border: "1px solid var(--line)", borderRadius: "var(--r-md)", padding: "1rem", background: "var(--bg-1)", fontFamily: "var(--font-mono)", fontSize: "0.8rem" }}>
      <div style={{ color: "var(--ink-2)", fontSize: "0.7rem", marginBottom: "0.45rem", letterSpacing: "0.04em" }}>TKT <span style={{ color: "var(--ink-0)" }}>{props.id}</span> · {props.customer}</div>
      <div style={{ color: "var(--ink-0)", fontFamily: "var(--font-body)", fontSize: "0.88rem", marginBottom: "0.85rem", lineHeight: 1.4, minHeight: "2.7em" }}>{props.summary}</div>
      <span style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem", fontSize: "0.7rem", padding: "0.28rem 0.55rem", borderRadius: 2, color: props.statusColor, background: props.statusBg, border: `1px solid ${props.statusColor}33` }}>
        <span style={{ width: 7, height: 7, borderRadius: 1, background: props.statusColor }} />
        {props.statusLabel}
      </span>
      <div style={{ marginTop: "0.85rem", paddingTop: "0.75rem", borderTop: "1px dashed var(--line-2)", fontSize: "0.72rem", color: "var(--ink-2)", fontStyle: "italic", lineHeight: 1.4, minHeight: "4.4em" }}>{props.note}</div>
      {props.buttons && (
        <div style={{ display: "flex", gap: "0.3rem", marginTop: "0.65rem", flexWrap: "wrap" }}>
          {props.buttons.map((b, i) => (
            <span key={b} style={{ fontFamily: "var(--font-mono)", fontSize: "0.65rem", padding: "0.25rem 0.5rem", border: i === 0 ? "1px solid var(--accent)" : "1px solid var(--line-2)", background: i === 0 ? "var(--accent)" : "transparent", color: i === 0 ? "var(--accent-ink)" : "var(--ink-1)", borderRadius: 2, fontWeight: i === 0 ? 700 : 400 }}>{b}</span>
          ))}
        </div>
      )}
    </div>
  );
}

function SkillsMarketing() {
  const items = [
    { name: "m365-onboarding", cat: "User lifecycle", desc: "New starter end-to-end: create account, assign licences, copy group memberships, set OOO, post welcome email.", autonomy: "approval-required", conf: 78 },
    { name: "offboarding", cat: "User lifecycle", desc: "Disable, revoke sessions, convert to shared mailbox, forwarding, remove licences, export OneDrive, post handover.", autonomy: "approval-required", conf: 84 },
    { name: "password-reset", cat: "Identity", desc: "Verify against IT Glue, reset via Graph, draft user-facing email, require approval before send.", autonomy: "auto-with-review", conf: 92 },
    { name: "mfa-setup", cat: "Identity", desc: "Walk users through Authenticator, reset stuck enrolments, screenshots-by-step in a private note.", autonomy: "approval-required", conf: 81 },
    { name: "shared-mailbox", cat: "Email", desc: "Create or grant access to a shared mailbox, set Send-As + Send-On-Behalf, auto-map to Outlook.", autonomy: "approval-required", conf: 79 },
    { name: "vpn-config", cat: "Network", desc: "Reads customer's IT Glue documentation for VPN topology, drafts setup steps per vendor.", autonomy: "supervised", conf: 64 },
    { name: "license-audit", cat: "Licensing", desc: "Compares CIPP/Graph counts against your contract, flags over-allocation and unassigned seats.", autonomy: "approval-required", conf: 71 },
    { name: "alert-investigation", cat: "Monitoring", desc: "Triages NinjaRMM alerts: cross-references history, runs diagnostics, classifies noise / signal / investigate.", autonomy: "approval-required", conf: 76 },
    { name: "+ 34 more", cat: "Multiple", desc: "Backup restore, DNS, signature management, conditional access, account compromise, send-as, retire device, monthly report — plus self-learning that writes new skills from your team's procedures.", autonomy: null as string | null, conf: null as number | null },
  ];
  return (
    <section id="skills" style={{ padding: "4rem 0 5rem", borderTop: "1px solid var(--line)" }}>
      <div style={{ maxWidth: 1180, margin: "0 auto", padding: "0 2rem" }}>
        <p className="eyebrow-accent" style={{ marginBottom: "0.6rem" }}>What Alan handles today</p>
        <h2 style={{ fontFamily: "var(--font-display)", fontSize: "clamp(2rem, 4vw, 3rem)", fontWeight: 700, letterSpacing: "-0.02em", marginBottom: "1rem", lineHeight: 1.08 }}>
          42 skills shipped today. Not a roadmap.
        </h2>
        <p style={{ color: "var(--ink-1)", maxWidth: "60ch", marginBottom: "2.5rem", lineHeight: 1.6 }}>
          Skills are markdown procedures with confidence thresholds and autonomy levels. Each earns its way from supervised to autonomous through consistent success. New skills are written by Alan when an engineer walks it through a process for the first time.
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", borderTop: "1px solid var(--line)", borderLeft: "1px solid var(--line)" }}>
          {items.map((s) => (
            <div key={s.name} style={{ padding: "1.25rem", borderRight: "1px solid var(--line)", borderBottom: "1px solid var(--line)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "0.5rem" }}>
                <div className="mono" style={{ color: "var(--ink-0)", fontSize: "0.92rem", fontWeight: 500 }}>{s.name}</div>
                <div className="eyebrow" style={{ fontSize: "0.65rem" }}>{s.cat}</div>
              </div>
              <div style={{ color: "var(--ink-1)", fontSize: "0.85rem", lineHeight: 1.5 }}>{s.desc}</div>
              {s.autonomy && (
                <div style={{ marginTop: "0.85rem", display: "flex", gap: "0.4rem", fontFamily: "var(--font-mono)", fontSize: "0.65rem", color: "var(--ink-2)" }}>
                  <span style={{ padding: "0.18rem 0.45rem", border: "1px solid var(--line-2)", borderRadius: 2 }}>{s.autonomy}</span>
                  <span style={{ padding: "0.18rem 0.45rem", border: "1px solid var(--line-2)", borderRadius: 2 }}>conf {s.conf}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Stack() {
  const items = [
    { name: "HaloPSA", role: "Source of truth", detail: "Tickets, actions, customers, KB, attachments. Full read/write via OAuth2." },
    { name: "Microsoft 365", role: "Tenant operations", detail: "User create / disable / licence / group ops via Graph API." },
    { name: "NinjaRMM", role: "Device context", detail: "Devices, orgs, alerts, software, last-logged-in user." },
    { name: "IT Glue", role: "Documentation", detail: "Read/write configurations, flexible assets, passwords (names only)." },
    { name: "Your inbox", role: "Email channel", detail: "Inbound mail at support@yourdomain auto-creates a Halo ticket and gets triaged." },
    { name: "Your runbooks", role: "Customer knowledge", detail: "Three-tier knowledge: user · customer · global." },
  ];
  return (
    <section style={{ padding: "4rem 0 5rem", borderTop: "1px solid var(--line)" }}>
      <div style={{ maxWidth: 1180, margin: "0 auto", padding: "0 2rem" }}>
        <p className="eyebrow-accent" style={{ marginBottom: "0.6rem" }}>Built for your stack</p>
        <h2 style={{ fontFamily: "var(--font-display)", fontSize: "clamp(2rem, 4vw, 3rem)", fontWeight: 700, letterSpacing: "-0.02em", marginBottom: "2.5rem", lineHeight: 1.08 }}>
          Talks to the tools you already pay for.
        </h2>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 1, background: "var(--line)", border: "1px solid var(--line)" }}>
          {items.map((s) => (
            <div key={s.name} style={{ background: "var(--bg-0)", padding: "1.5rem" }}>
              <div style={{ fontFamily: "var(--font-display)", fontSize: "1.3rem", fontWeight: 700, marginBottom: "0.4rem" }}>{s.name}</div>
              <div className="mono" style={{ color: "var(--accent)", fontSize: "0.72rem", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: "0.85rem" }}>{s.role}</div>
              <div style={{ color: "var(--ink-1)", fontSize: "0.88rem", lineHeight: 1.55 }}>{s.detail}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Safety() {
  const items = [
    { num: "01", title: "Email approval gate", body: "Every outbound customer email blocks on a human click. Enforced at the tool dispatcher, not the prompt: even a model that ignores instructions cannot bypass it.", spec: "approved_email: true required" },
    { num: "02", title: "Kill switch", body: "One toggle. Suspends every poller, drains the ticket-job queue, returns active workflow instances to engineers within 30 seconds.", spec: "tenants.killswitch flag" },
    { num: "03", title: "Dry-run mode", body: "All write tool calls are intercepted and recorded as 'would have done X' audit entries. No external API gets a mutating request. Every new tenant starts here for 14 days.", spec: "tenants.dry_run flag" },
    { num: "04", title: "Autonomy graduation", body: "Skills run supervised until 50 approvals + 0 rejects → approval-required. Then 200 approvals + <5% reject rate → auto-with-review. Reject patterns auto-demote.", spec: "default_autonomy enum, 4 levels" },
    { num: "05", title: "Hash-chained audit", body: "Every action, override, integration credential change. SHA-256 chain over canonical JSON per tenant. Tampering breaks the chain at the modified row.", spec: "365-day retention minimum" },
    { num: "06", title: "Triage Priority Engine", body: "ITIL 4 Impact × Urgency 1-5 each → priority 1-4. Modulators: VIP floor=P2, 24x7+OOH bump, storm master=P1/child=P4, breach keywords pin P1.", spec: "deterministic, fully traced" },
    { num: "07", title: "Storm coalescing", body: "≥3 tickets from one customer with overlapping noun-phrase signature in 15 minutes opens a storm. First ticket becomes master; siblings silenced to P4.", spec: "KV-backed, D1-persisted" },
    { num: "08", title: "AES-GCM credential vault", body: "Every third-party token (HaloPSA refresh, NinjaRMM secret, IT Glue key) AES-GCM 256 encrypted at rest. Master key lives as a Worker Secret, never in D1 or logs.", spec: "key version byte enables rotation" },
    { num: "09", title: "Tenant region locking", body: "UK (default), EU, or US — fixed at sign-up. All compute, storage, and AI inference for that tenant stays in the chosen region.", spec: "Cloudflare data localisation" },
    { num: "10", title: "Bedrock for AI", body: "Claude Haiku 4.5 and Sonnet 4.7 via AWS Bedrock eu-west-2. Anthropic does not train on Bedrock prompts. Zero-retention abuse-monitoring opt-out requested; status disclosed in /security.", spec: "model traffic stays in-region" },
    { num: "11", title: "GDPR Article 28", body: "DPA available pre-signature at /legal/dpa. 30-day sub-processor change notice. 72-hour breach notification. 30-day post-termination deletion with audit-log carve-out.", spec: "controller-processor terms" },
    { num: "12", title: "What we don't do", body: "No analytics SDKs. No tracking pixels. No model-training data sharing. No third-party support tools touching your data. No backdoors.", spec: "by absence, by design" },
  ];
  return (
    <section id="safety" style={{ padding: "4rem 0 5rem", borderTop: "1px solid var(--line)" }}>
      <div style={{ maxWidth: 1180, margin: "0 auto", padding: "0 2rem" }}>
        <p className="eyebrow-accent" style={{ marginBottom: "0.6rem" }}>Safety &amp; governance</p>
        <h2 style={{ fontFamily: "var(--font-display)", fontSize: "clamp(2rem, 4vw, 3rem)", fontWeight: 700, letterSpacing: "-0.02em", marginBottom: "1rem", lineHeight: 1.08 }}>
          Twelve concrete controls. No marketing slogans.
        </h2>
        <p style={{ color: "var(--ink-1)", maxWidth: "62ch", marginBottom: "2.5rem", lineHeight: 1.6, fontSize: "0.98rem" }}>
          These are the controls implemented today — names of the actual mechanisms, not abstract promises. The full spec sheet is at <Link to="/security" style={{ color: "var(--accent)" }}>/security</Link>; the privacy notice and DPA are at <Link to="/legal/privacy" style={{ color: "var(--accent)" }}>/legal</Link>.
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(290px, 1fr))", gap: "1rem" }}>
          {items.map((s) => (
            <div key={s.num} style={{ position: "relative", padding: "1.25rem 1.4rem", border: "1px solid var(--line)", background: "var(--bg-1)", borderRadius: "var(--r-md)" }}>
              <div style={{ position: "absolute", top: 0, left: 0, width: 2, height: "100%", background: "var(--accent)", opacity: 0.45 }} />
              <div className="mono" style={{ color: "var(--accent)", fontSize: "0.68rem", letterSpacing: "0.1em", marginBottom: "0.55rem" }}>{s.num}</div>
              <div style={{ fontFamily: "var(--font-display)", fontSize: "1.05rem", fontWeight: 700, marginBottom: "0.4rem", lineHeight: 1.25 }}>{s.title}</div>
              <div style={{ color: "var(--ink-1)", fontSize: "0.85rem", lineHeight: 1.55, marginBottom: "0.65rem" }}>{s.body}</div>
              <div className="mono" style={{ color: "var(--ink-2)", fontSize: "0.7rem", letterSpacing: "0.04em", paddingTop: "0.55rem", borderTop: "1px dashed var(--line-2)" }}>{s.spec}</div>
            </div>
          ))}
        </div>
        <p className="mono" style={{ textAlign: "center", marginTop: "1.75rem", color: "var(--ink-2)", fontSize: "0.78rem" }}>
          Full spec sheet → <Link to="/security" style={{ color: "var(--accent)" }}>/security</Link> · DPA → <Link to="/legal/dpa" style={{ color: "var(--accent)" }}>/legal/dpa</Link> · Sub-processors → <Link to="/legal/subprocessors" style={{ color: "var(--accent)" }}>/legal/subprocessors</Link>
        </p>
      </div>
    </section>
  );
}

function Pricing() {
  return (
    <section id="pricing" style={{ padding: "4rem 0 5rem", borderTop: "1px solid var(--line)" }}>
      <div style={{ maxWidth: 1180, margin: "0 auto", padding: "0 2rem" }}>
        <p className="eyebrow-accent" style={{ marginBottom: "0.6rem" }}>Pricing</p>
        <h2 style={{ fontFamily: "var(--font-display)", fontSize: "clamp(2rem, 4vw, 3rem)", fontWeight: 700, letterSpacing: "-0.02em", marginBottom: "2.5rem", lineHeight: 1.08 }}>
          One number. Self-serve checkout. No "contact sales".
        </h2>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "2rem", alignItems: "center" }}>
          <div style={{ position: "relative", padding: "2.5rem", border: "1px solid var(--accent-line)", borderRadius: "var(--r-md)", background: "linear-gradient(180deg, var(--bg-2), var(--bg-1))" }}>
            <div className="mono" style={{ color: "var(--accent)", fontSize: "0.7rem", letterSpacing: "0.16em", textTransform: "uppercase", marginBottom: "1.25rem" }}>Alan / standard</div>
            <div style={{ display: "flex", alignItems: "baseline", gap: "0.4rem", marginBottom: "0.4rem" }}>
              <span style={{ fontFamily: "var(--font-display)", fontSize: "4rem", fontWeight: 700, letterSpacing: "-0.03em", lineHeight: 1 }}>£50</span>
              <span style={{ color: "var(--ink-1)", fontSize: "1.05rem" }}>/ tech / month</span>
            </div>
            <div style={{ color: "var(--ink-1)", fontSize: "0.95rem", marginBottom: "1.75rem" }}>Billed monthly. No setup fee. 14-day dry-run pilot included.</div>
            <a href={platformHref("/sign-in")} style={{ display: "block", padding: "0.85rem", background: "var(--accent)", color: "var(--accent-ink)", textAlign: "center", borderRadius: "var(--r-sm)", fontWeight: 700 }}>Start the 14-day pilot →</a>
          </div>
          <ul className="mono" style={{ listStyle: "none", padding: 0, fontSize: "0.85rem", color: "var(--ink-2)", lineHeight: 1.7 }}>
            <li style={{ marginBottom: "1rem" }}><b style={{ color: "var(--ink-0)" }}>Worked example.</b> 10-tech MSP pays <b style={{ color: "var(--ink-0)" }}>£500/mo</b>. If Alan handles 30% of L1, that's roughly <b style={{ color: "var(--ink-0)" }}>30–40 engineer-hours</b> reclaimed.</li>
            <li style={{ marginBottom: "1rem" }}><b style={{ color: "var(--ink-0)" }}>Bedrock costs.</b> Typically £40–£120/mo for a 10-tech shop. Billed by AWS directly.</li>
            <li style={{ marginBottom: "1rem" }}><b style={{ color: "var(--ink-0)" }}>No lock-in.</b> Monthly billing. Cancel and keep your data + runbooks.</li>
          </ul>
        </div>
      </div>
    </section>
  );
}

function FAQ() {
  const items = [
    { q: "What happens when Alan gets something wrong?", a: "Nothing destructive happens without approval. The error gets logged. If you click Reject or Feedback, that signal feeds back into the next triage. Skills that get rejected too often automatically drop back to supervised." },
    { q: "Where does my customer data go?", a: "On Cloudflare's edge in your chosen region. AI calls go from Cloudflare directly to your Bedrock tenancy. Nothing routes through Dead Simple Computing servers." },
    { q: "What does the pilot look like?", a: "14 days, dry-run mode, no billing. We jump on a 90-min call, wire Alan to your Halo, watch what it would have done on your last 50 tickets. If you're happy, flip dry-run off and the £50/tech starts." },
  ];
  return (
    <section style={{ padding: "4rem 0 5rem", borderTop: "1px solid var(--line)" }}>
      <div style={{ maxWidth: 880, margin: "0 auto", padding: "0 2rem" }}>
        <p className="eyebrow-accent" style={{ marginBottom: "0.6rem" }}>Questions, mostly the same ones</p>
        <h2 style={{ fontFamily: "var(--font-display)", fontSize: "clamp(2rem, 4vw, 3rem)", fontWeight: 700, letterSpacing: "-0.02em", marginBottom: "2.5rem", lineHeight: 1.08 }}>
          The questions every MSP asks before they trust this with their tenant.
        </h2>
        <div style={{ borderTop: "1px solid var(--line)" }}>
          {items.map((q) => (
            <details key={q.q} style={{ borderBottom: "1px solid var(--line)", padding: "1.5rem 0" }}>
              <summary style={{ cursor: "pointer", listStyle: "none", display: "flex", justifyContent: "space-between", gap: "1rem", fontFamily: "var(--font-display)", fontSize: "1.15rem", fontWeight: 700 }}>
                {q.q}<span style={{ color: "var(--accent)" }}>+</span>
              </summary>
              <p style={{ marginTop: "1rem", color: "var(--ink-1)", lineHeight: 1.7, maxWidth: "70ch" }}>{q.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section style={{ padding: "5rem 0", borderTop: "1px solid var(--line)", borderBottom: "1px solid var(--line)" }}>
      <div style={{ maxWidth: 720, margin: "0 auto", padding: "0 2rem", textAlign: "center" }}>
        <h2 style={{ fontFamily: "var(--font-display)", fontSize: "clamp(2.2rem, 5vw, 3.5rem)", fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1.05, marginBottom: "1.25rem" }}>
          Pilot it on your real tickets for 14 days. Dry-run by default.
        </h2>
        <a href={platformHref("/sign-in")} style={{ display: "inline-block", padding: "0.95rem 1.75rem", background: "var(--accent)", color: "var(--accent-ink)", borderRadius: "var(--r-sm)", fontWeight: 700, fontSize: "1.05rem" }}>Get started →</a>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer style={{ padding: "3rem 0 4rem", fontFamily: "var(--font-mono)", fontSize: "0.78rem", color: "var(--ink-2)", borderTop: "1px solid var(--line)" }}>
      <div style={{ maxWidth: 1180, margin: "0 auto", padding: "0 2rem", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
        <div><span style={{ color: "var(--ink-0)" }}>Alan</span> · an autonomous service-desk agent for HaloPSA MSPs</div>
        <nav style={{ display: "flex", gap: "1.25rem", alignItems: "center" }}>
          <Link to="/security" style={{ color: "var(--ink-1)" }}>Security</Link>
          <Link to="/legal/privacy" style={{ color: "var(--ink-1)" }}>Privacy</Link>
          <Link to="/legal/terms" style={{ color: "var(--ink-1)" }}>Terms</Link>
          <Link to="/legal/dpa" style={{ color: "var(--ink-1)" }}>DPA</Link>
          <Link to="/legal/subprocessors" style={{ color: "var(--ink-1)" }}>Sub-processors</Link>
        </nav>
        <div style={{ letterSpacing: "0.04em" }}>© 2026 Dead Simple Computing Ltd · Reading, UK</div>
      </div>
    </footer>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Sign-in
// ─────────────────────────────────────────────────────────────────────

function SignIn() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null); setBusy(true);
    try {
      const res = await fetch("/auth/magic", { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ email }) });
      if (res.status === 429) { setError("Too many sign-in attempts from your network. Please wait a few minutes and try again."); return; }
      const data = (await res.json().catch(() => ({}))) as { sent?: boolean; error?: string; detail?: string };
      if (res.status >= 500) { setError(data.detail ?? "Something went wrong on our end. Please try again in a moment."); return; }
      if (!data.sent) { setError(data.detail ?? data.error ?? "Could not send sign-in link"); return; }
      navigate(`/sign-in/sent?email=${encodeURIComponent(email)}`);
    } catch (err) { setError((err as Error).message); } finally { setBusy(false); }
  }
  return (
    <main style={{ padding: "5rem 2rem", maxWidth: 460, margin: "0 auto" }}>
      <p className="eyebrow">Step 1 / 5</p>
      <h1 style={{ fontFamily: "var(--font-display)", fontSize: "2.25rem", margin: "0.75rem 0 0.5rem" }}>Sign in to Alan</h1>
      <p style={{ color: "var(--ink-1)", marginBottom: "2rem" }}>We'll email you a link to sign in. Valid for 15 minutes, one use only.</p>
      <form onSubmit={submit}>
        <label style={{ display: "block", marginBottom: "1rem" }}>
          <span className="eyebrow" style={{ display: "block", marginBottom: "0.4rem" }}>Work email</span>
          <input type="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@yourmsp.co.uk" style={{ width: "100%", padding: "0.85rem 1rem", background: "var(--bg-2)", border: "1px solid var(--line-2)", borderRadius: "var(--r-md)", color: "var(--ink-0)", fontSize: "1rem", outline: "none" }} />
        </label>
        {error && <p style={{ padding: "0.75rem 1rem", background: "var(--danger-soft)", border: "1px solid var(--danger)", borderRadius: "var(--r-md)", color: "var(--danger)", marginBottom: "1rem", fontSize: "0.9rem" }}>{error}</p>}
        <button type="submit" disabled={busy || !email} style={{ width: "100%", padding: "0.85rem 1rem", background: busy ? "var(--bg-3)" : "var(--accent)", color: "var(--accent-ink)", border: "none", borderRadius: "var(--r-md)", fontSize: "1rem", fontWeight: 700, cursor: busy ? "not-allowed" : "pointer" }}>{busy ? "Sending…" : "Send me a sign-in link"}</button>
      </form>
    </main>
  );
}

function SignInSent() {
  const [params] = useSearchParams();
  const email = params.get("email") ?? "your inbox";
  return (
    <main style={{ padding: "5rem 2rem", maxWidth: 460, margin: "0 auto" }}>
      <p className="eyebrow-accent">Link sent</p>
      <h1 style={{ fontFamily: "var(--font-display)", fontSize: "2.25rem", margin: "0.75rem 0 0.5rem" }}>Check {email}</h1>
      <p style={{ color: "var(--ink-1)" }}>We've sent you a sign-in link. Valid for 15 minutes, one use only. If you don't see it in two minutes, check spam, or <Link to="/sign-in" style={{ color: "var(--accent)" }}>try again</Link>.</p>
    </main>
  );
}

// ─────────────────────────────────────────────────────────────────────
// App shell (sidebar + outlet)
// ─────────────────────────────────────────────────────────────────────

interface MeResponse {
  user_id: string;
  tenant_id: string;
  session_id: string;
}

interface TenantResponse {
  tenant: {
    id: string;
    name: string;
    slug: string;
    plan: string;
    seats_licensed: number;
    seats_used: number;
    dry_run: boolean;
    killswitch: boolean;
    data_region: string;
  };
}

function useMe(): { me: MeResponse | null; loading: boolean; error: string | null } {
  const [me, setMe] = useState<MeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch("/api/me", { credentials: "include" });
        if (r.status === 401) { setError("unauthenticated"); setLoading(false); return; }
        if (!r.ok) { setError(`HTTP ${r.status}`); setLoading(false); return; }
        setMe(await r.json());
      } catch (err) { setError((err as Error).message); }
      finally { setLoading(false); }
    })();
  }, []);
  return { me, loading, error };
}

function AppShell() {
  const { me, loading, error } = useMe();
  if (loading) return <FullPageLoader label="Loading workspace…" />;
  if (error === "unauthenticated") return <Navigate to="/sign-in" replace />;
  if (error) return <FullPageError message={error} />;
  void me;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "240px 1fr", minHeight: "100vh" }}>
      <Sidebar />
      <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
        <SafetyBanner />
        <main style={{ background: "var(--bg-0)", padding: "2.5rem 3rem", overflow: "auto", flex: 1 }}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Safety banner — kill switch + dry-run countdown, visible on every page
// ─────────────────────────────────────────────────────────────────────

interface DryRunFloor {
  floor_days: number;
  available_at: number;
  days_remaining: number;
  unlockable_now: boolean;
  current: boolean;
}

function SafetyBanner() {
  const [tenant, setTenant] = useState<TenantResponse["tenant"] | null>(null);
  const [floor, setFloor] = useState<DryRunFloor | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    try {
      const [t, f] = await Promise.all([
        fetch("/api/settings/tenant", { credentials: "include" }).then((r) => r.json()) as Promise<TenantResponse>,
        fetch("/api/settings/dry-run-floor", { credentials: "include" }).then((r) => r.json()) as Promise<DryRunFloor>,
      ]);
      setTenant(t.tenant);
      setFloor(f);
    } catch {
      // Banner is best-effort; never blocks navigation.
    }
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  async function toggleKillswitch(active: boolean) {
    setBusy(true);
    try {
      await fetch("/api/settings/killswitch", {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ active }),
      });
      void reload();
    } finally { setBusy(false); }
  }

  if (!tenant) return null;

  // Highest priority: killswitch active → red, blocking-looking banner.
  if (tenant.killswitch) {
    return (
      <div role="alert" style={{
        background: "var(--danger-soft)",
        borderBottom: "2px solid var(--danger)",
        color: "var(--ink-0)",
        padding: "0.85rem 2rem",
        display: "flex",
        alignItems: "center",
        gap: "1rem",
        fontSize: "0.92rem",
      }}>
        <span style={{ display: "inline-flex", width: 10, height: 10, borderRadius: 999, background: "var(--danger)", boxShadow: "0 0 0 4px var(--danger-soft)" }} />
        <span style={{ flex: 1 }}>
          <strong style={{ color: "var(--danger)" }}>Kill switch active.</strong> Every poller is suspended, every job is drained back to engineers. Nothing Alan can do touches Halo or any integration until you reactivate.
        </span>
        <button onClick={() => toggleKillswitch(false)} disabled={busy} className="primary-button" style={{ background: "var(--danger)", color: "var(--ink-0)", padding: "0.45rem 0.9rem", fontSize: "0.82rem" }}>
          {busy ? "Reactivating…" : "Reactivate Alan"}
        </button>
      </div>
    );
  }

  // Next: dry-run during the 30-day floor → amber notice with countdown +
  // disabled "Go live" button explaining why.
  if (tenant.dry_run && floor && !floor.unlockable_now) {
    return (
      <div role="status" style={{
        background: "var(--warn-soft)",
        borderBottom: "1px solid var(--warn)",
        color: "var(--ink-0)",
        padding: "0.7rem 2rem",
        display: "flex",
        alignItems: "center",
        gap: "1rem",
        fontSize: "0.88rem",
      }}>
        <span style={{ display: "inline-flex", width: 8, height: 8, borderRadius: 999, background: "var(--warn)" }} />
        <span style={{ flex: 1 }}>
          <strong style={{ color: "var(--warn)" }}>Dry-run · {floor.days_remaining} day{floor.days_remaining === 1 ? "" : "s"} remaining.</strong>{" "}
          Alan reads tickets, proposes solutions, and drafts replies. Nothing is sent. Every action is logged and flagged for review. Locked until {new Date(floor.available_at).toLocaleDateString("en-GB")}.
        </span>
        <button onClick={() => toggleKillswitch(true)} disabled={busy} className="ghost-button" style={{ padding: "0.4rem 0.85rem", fontSize: "0.78rem", borderColor: "var(--danger-line, var(--line-2))", color: "var(--ink-1)" }}>
          Kill switch
        </button>
      </div>
    );
  }

  // Dry-run + unlockable: amber but with the "go live" hint.
  if (tenant.dry_run && floor?.unlockable_now) {
    return (
      <div role="status" style={{
        background: "var(--warn-soft)",
        borderBottom: "1px solid var(--warn)",
        color: "var(--ink-0)",
        padding: "0.7rem 2rem",
        display: "flex",
        alignItems: "center",
        gap: "1rem",
        fontSize: "0.88rem",
      }}>
        <span style={{ display: "inline-flex", width: 8, height: 8, borderRadius: 999, background: "var(--warn)" }} />
        <span style={{ flex: 1 }}>
          <strong style={{ color: "var(--warn)" }}>Dry-run is unlockable.</strong> Your 30-day floor has passed. Flip the toggle in Settings → Mode when you're ready for Alan to start executing approved actions.
        </span>
        <button onClick={() => toggleKillswitch(true)} disabled={busy} className="ghost-button" style={{ padding: "0.4rem 0.85rem", fontSize: "0.78rem", color: "var(--ink-1)" }}>
          Kill switch
        </button>
      </div>
    );
  }

  // Live mode — show a thin green strip + a one-click kill switch.
  return (
    <div role="status" style={{
      background: "var(--ok-soft)",
      borderBottom: "1px solid var(--ok)",
      color: "var(--ink-0)",
      padding: "0.5rem 2rem",
      display: "flex",
      alignItems: "center",
      gap: "1rem",
      fontSize: "0.82rem",
    }}>
      <span style={{ display: "inline-flex", width: 7, height: 7, borderRadius: 999, background: "var(--ok)" }} />
      <span style={{ flex: 1, color: "var(--ink-1)" }}>
        <strong style={{ color: "var(--ok)" }}>Live.</strong> Alan can execute approved actions. Every email pauses for engineer approval.
      </span>
      <button onClick={() => toggleKillswitch(true)} disabled={busy} className="ghost-button" style={{ padding: "0.35rem 0.85rem", fontSize: "0.74rem", borderColor: "var(--danger)", color: "var(--danger)" }}>
        {busy ? "Activating…" : "Kill switch"}
      </button>
    </div>
  );
}

function Sidebar() {
  const navItem = (props: { to: string; label: string; eyebrow?: string }) => (
    <NavLink
      to={props.to}
      end={props.to === "/app"}
      style={({ isActive }) => ({
        display: "block",
        padding: "0.6rem 0.85rem",
        marginBottom: "0.2rem",
        borderRadius: "var(--r-sm)",
        color: isActive ? "var(--accent-ink)" : "var(--ink-1)",
        background: isActive ? "var(--accent)" : "transparent",
        fontWeight: isActive ? 700 : 500,
        fontSize: "0.92rem",
        textDecoration: "none",
        transition: "background 0.12s",
      })}
    >
      {props.label}
    </NavLink>
  );
  return (
    <aside style={{ background: "var(--bg-1)", borderRight: "1px solid var(--line)", padding: "1.5rem 1rem", display: "flex", flexDirection: "column" }}>
      <Link to="/" style={{ display: "flex", alignItems: "baseline", gap: "0.65rem", marginBottom: "2.5rem", padding: "0 0.85rem" }}>
        <span style={{ fontFamily: "var(--font-display)", fontSize: "1.3rem", fontWeight: 700, letterSpacing: "-0.02em" }}>
          Alan<span style={{ color: "var(--accent)" }}>.</span>
        </span>
      </Link>
      <p className="eyebrow" style={{ padding: "0 0.85rem", marginBottom: "0.5rem" }}>Workspace</p>
      {navItem({ to: "/app", label: "Live ops" })}
      {navItem({ to: "/app/overview", label: "Overview" })}
      {navItem({ to: "/app/integrations", label: "Integrations" })}
      {navItem({ to: "/app/skills", label: "Skills" })}
      {navItem({ to: "/app/try-alan", label: "Try Alan" })}
      <p className="eyebrow" style={{ padding: "0 0.85rem", marginBottom: "0.5rem", marginTop: "1.5rem" }}>Intelligence</p>
      {navItem({ to: "/app/sentiment", label: "Sentiment" })}
      {navItem({ to: "/app/qa", label: "QA" })}
      {navItem({ to: "/app/reports", label: "Reports & QBR" })}
      <p className="eyebrow" style={{ padding: "0 0.85rem", marginBottom: "0.5rem", marginTop: "1.5rem" }}>Operations</p>
      {navItem({ to: "/app/team", label: "Team" })}
      {navItem({ to: "/app/alerts", label: "Alerts" })}
      {navItem({ to: "/app/security", label: "Security" })}
      {navItem({ to: "/app/audit", label: "Audit log" })}
      {navItem({ to: "/app/settings", label: "Settings" })}
      <div style={{ marginTop: "auto", padding: "1rem 0.85rem", borderTop: "1px solid var(--line)" }}>
        <button onClick={async () => { await fetch("/auth/signout", { method: "POST", credentials: "include" }); window.location.href = "/"; }} style={{ background: "transparent", color: "var(--ink-2)", border: "none", padding: 0, fontSize: "0.85rem", cursor: "pointer", fontFamily: "inherit" }}>
          ← Sign out
        </button>
      </div>
    </aside>
  );
}

function PageHeader(props: { eyebrow: string; title: string; subtitle?: string; action?: React.ReactNode }) {
  return (
    <header style={{ marginBottom: "2.5rem", display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: "2rem", flexWrap: "wrap" }}>
      <div>
        <p className="eyebrow-accent" style={{ marginBottom: "0.4rem" }}>{props.eyebrow}</p>
        <h1 style={{ fontFamily: "var(--font-display)", fontSize: "2.2rem", fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1.08, marginBottom: props.subtitle ? "0.5rem" : 0 }}>{props.title}</h1>
        {props.subtitle && <p style={{ color: "var(--ink-1)", maxWidth: "60ch", fontSize: "0.95rem" }}>{props.subtitle}</p>}
      </div>
      {props.action}
    </header>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Overview page
// ─────────────────────────────────────────────────────────────────────

function Overview() {
  const [tenant, setTenant] = useState<TenantResponse["tenant"] | null>(null);
  const [integrations, setIntegrations] = useState<Array<{ provider: string; status: string }>>([]);
  useEffect(() => {
    (async () => {
      try {
        const [t, i] = await Promise.all([
          fetch("/api/settings/tenant", { credentials: "include" }).then((r) => r.json()) as Promise<TenantResponse>,
          fetch("/api/onboarding/integrations", { credentials: "include" }).then((r) => r.json()) as Promise<{ integrations: Array<{ provider: string; status: string }> }>,
        ]);
        setTenant(t.tenant);
        setIntegrations(i.integrations);
      } catch { /* surfaced below */ }
    })();
  }, []);
  return (
    <>
      <PageHeader eyebrow="Workspace" title={tenant ? tenant.name : "Loading…"} subtitle={tenant ? `slug: ${tenant.slug} · plan: ${tenant.plan} · region: ${tenant.data_region.toUpperCase()}` : undefined} />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "1rem", marginBottom: "2.5rem" }}>
        <Stat label="Mode" value={tenant?.dry_run ? "Dry-run" : tenant?.killswitch ? "Kill switch active" : "Live"} accent={tenant?.killswitch ? "var(--danger)" : tenant?.dry_run ? "var(--warn)" : "var(--ok)"} />
        <Stat label="Seats" value={tenant ? `${tenant.seats_used} / ${tenant.seats_licensed}` : "—"} />
        <Stat label="Integrations active" value={`${integrations.filter((i) => i.status === "active").length} / 4`} />
        <Stat label="Tickets processed today" value="0" sub="No tenants polling yet" />
      </div>
      <Card title="Getting started" subtitle="Three things to do before your first ticket flows through Alan.">
        <ChecklistItem done={!!tenant} label="Workspace created" />
        <ChecklistItem done={integrations.some((i) => i.provider === "halopsa" && i.status === "active")} label="Connect HaloPSA" href="/app/integrations" />
        <ChecklistItem done={integrations.some((i) => i.provider === "graph" && i.status === "active")} label="Connect Microsoft 365" href="/app/integrations" />
        <ChecklistItem done={!tenant?.dry_run} label="Leave dry-run mode (after 14-day pilot)" href="/app/settings" />
      </Card>
    </>
  );
}

function Stat(props: { label: string; value: string; sub?: string; accent?: string }) {
  return (
    <div style={{ padding: "1.25rem 1.4rem", border: "1px solid var(--line)", borderRadius: "var(--r-md)", background: "var(--bg-1)" }}>
      <div className="eyebrow" style={{ marginBottom: "0.4rem" }}>{props.label}</div>
      <div style={{ fontFamily: "var(--font-display)", fontSize: "1.6rem", fontWeight: 700, color: props.accent ?? "var(--ink-0)", lineHeight: 1.1 }}>{props.value}</div>
      {props.sub && <div style={{ color: "var(--ink-2)", fontSize: "0.78rem", marginTop: "0.35rem" }}>{props.sub}</div>}
    </div>
  );
}

function Card(props: { title: string; subtitle?: string; children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <section style={{ border: "1px solid var(--line)", borderRadius: "var(--r-md)", background: "var(--bg-1)", padding: "1.5rem 1.75rem", marginBottom: "1.5rem" }}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1.25rem", gap: "1rem" }}>
        <div>
          <h2 style={{ fontFamily: "var(--font-display)", fontSize: "1.3rem", fontWeight: 700, letterSpacing: "-0.01em" }}>{props.title}</h2>
          {props.subtitle && <p style={{ color: "var(--ink-1)", fontSize: "0.92rem", marginTop: "0.25rem" }}>{props.subtitle}</p>}
        </div>
        {props.action}
      </header>
      {props.children}
    </section>
  );
}

function ChecklistItem(props: { done: boolean; label: string; href?: string }) {
  const content = (
    <div style={{ display: "flex", alignItems: "center", gap: "0.85rem", padding: "0.65rem 0", borderTop: "1px solid var(--line)" }}>
      <span style={{ display: "inline-flex", justifyContent: "center", alignItems: "center", width: 22, height: 22, borderRadius: "50%", border: `1.5px solid ${props.done ? "var(--ok)" : "var(--line-2)"}`, background: props.done ? "var(--ok-soft)" : "transparent", color: "var(--ok)", fontFamily: "var(--font-mono)", fontSize: "0.75rem", fontWeight: 700 }}>
        {props.done ? "✓" : ""}
      </span>
      <span style={{ flex: 1, color: props.done ? "var(--ink-2)" : "var(--ink-0)", textDecoration: props.done ? "line-through" : "none" }}>{props.label}</span>
      {props.href && !props.done && <Link to={props.href} style={{ color: "var(--accent)", fontSize: "0.85rem" }}>Set up →</Link>}
    </div>
  );
  return content;
}

// ─────────────────────────────────────────────────────────────────────
// Integrations page
// ─────────────────────────────────────────────────────────────────────

interface Integration {
  provider: "halopsa" | "graph" | "ninja" | "itglue";
  base_url: string | null;
  status: string;
  last_token_refresh: number | null;
  last_health_check: number | null;
  last_error: string | null;
  installed_at: number;
}

function Integrations() {
  const [list, setList] = useState<Integration[]>([]);
  const [loading, setLoading] = useState(true);
  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch("/api/onboarding/integrations", { credentials: "include" }).then((r) => r.json()) as { integrations: Integration[] };
      setList(r.integrations);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  const integrations = [
    { provider: "halopsa", name: "HaloPSA", role: "Source of truth", desc: "Tickets, actions, customers. Bootstrap creates the 5 statuses + 7 outcomes Alan needs.", connectable: true },
    { provider: "ninja", name: "NinjaRMM", role: "Device context", desc: "Device summary, alerts, last-logged-in-user. Script execution behind engineer approval.", connectable: false, comingSoon: "Phase 2" },
    { provider: "itglue", name: "IT Glue", role: "Documentation", desc: "Read/write configurations, flexible assets, passwords (names only).", connectable: true, apiKeyOnly: true },
  ] as const;
  return (
    <>
      <PageHeader eyebrow="Integrations" title="Connect your stack" subtitle="Each integration uses OAuth where possible. Tokens are AES-GCM encrypted at rest with a per-Worker master key and only decrypted in request scope." />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))", gap: "1.25rem" }}>
        {integrations.map((cfg) => {
          const live = list.find((l) => l.provider === cfg.provider);
          return (
            <IntegrationCard key={cfg.provider} cfg={cfg} live={live} onChange={reload} />
          );
        })}
      </div>
      <M365Card />
      {loading && <p className="eyebrow" style={{ marginTop: "1.5rem" }}>Refreshing…</p>}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Microsoft 365 — MSP mode (multi-tenant Entra app + GDAP downstream tenants)
// ─────────────────────────────────────────────────────────────────────

interface GraphConfigState { configured: boolean; source: "tenant" | "env" | null; client_id?: string }
interface CustomerTenant { id: string; ms_tenant_id: string; display_name: string | null; halo_customer_id: number | null; status: string }

function M365Card() {
  const [cfg, setCfg] = useState<GraphConfigState | null>(null);
  const [customers, setCustomers] = useState<CustomerTenant[]>([]);
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [editApp, setEditApp] = useState(false);
  const [manualMs, setManualMs] = useState("");
  const [manualName, setManualName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    const [c, cust] = await Promise.all([
      fetch("/api/onboarding/integrations/graph/config", { credentials: "include" }).then((r) => r.json()) as Promise<GraphConfigState>,
      fetch("/api/onboarding/integrations/graph/customers", { credentials: "include" }).then((r) => r.json()) as Promise<{ customers: CustomerTenant[] }>,
    ]);
    setCfg(c);
    setCustomers(cust.customers);
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  async function saveApp() {
    setBusy(true); setError(null);
    try {
      const r = await fetch("/api/onboarding/integrations/graph/config", {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ client_id: clientId, client_secret: clientSecret }),
      });
      const d = await r.json().catch(() => ({})) as { ok?: boolean; error?: string; detail?: string };
      if (!d.ok) { setError(d.detail ?? d.error ?? "Could not save"); return; }
      setClientSecret(""); setEditApp(false); void reload();
    } finally { setBusy(false); }
  }

  async function addViaConsent() {
    setBusy(true); setError(null);
    try {
      const r = await fetch("/api/onboarding/integrations/graph/consent-url", {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: "{}",
      });
      const d = await r.json().catch(() => ({})) as { consent_url?: string; error?: string; detail?: string };
      if (d.error) { setError(d.detail ?? d.error); return; }
      if (d.consent_url) window.location.href = d.consent_url;
    } catch (err) { setError((err as Error).message); }
    finally { setBusy(false); }
  }

  async function addManual() {
    setBusy(true); setError(null);
    try {
      const r = await fetch("/api/onboarding/integrations/graph/customers/link", {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ms_tenant_id: manualMs, display_name: manualName || undefined }),
      });
      const d = await r.json().catch(() => ({})) as { id?: string; error?: string; detail?: string };
      if (d.error) { setError(d.detail ?? d.error); return; }
      setManualMs(""); setManualName(""); void reload();
    } finally { setBusy(false); }
  }

  async function mapHalo(id: string, value: string) {
    const n = Number.parseInt(value, 10);
    if (!Number.isFinite(n)) return;
    await fetch(`/api/onboarding/integrations/graph/customers/${id}/map`, {
      method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ halo_customer_id: n }),
    });
    void reload();
  }

  async function unlink(id: string) {
    await fetch(`/api/onboarding/integrations/graph/customers/${id}`, { method: "DELETE", credentials: "include" });
    void reload();
  }

  if (!cfg) return null;
  const consentRedirect = typeof window !== "undefined" ? `${window.location.origin}/oauth/graph/consent-callback` : "";

  return (
    <article style={{ border: "1px solid var(--line)", borderRadius: "var(--r-md)", background: "var(--bg-1)", padding: "1.5rem 1.75rem", marginTop: "1.25rem" }}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "0.5rem" }}>
        <div>
          <h3 style={{ fontFamily: "var(--font-display)", fontSize: "1.3rem", fontWeight: 700 }}>Microsoft 365 — MSP mode</h3>
          <p className="mono" style={{ color: "var(--accent)", fontSize: "0.7rem", letterSpacing: "0.08em", textTransform: "uppercase", marginTop: "0.2rem" }}>Tenant operations · multi-tenant GDAP</p>
        </div>
        <StatusPill status={cfg.configured ? "active" : "not-connected"} />
      </header>
      <p style={{ color: "var(--ink-1)", fontSize: "0.9rem", lineHeight: 1.55, marginBottom: "1.25rem" }}>
        Register one multi-tenant Entra app, then grant admin consent in each customer's Microsoft 365 tenant. Alan acts in each tenant with app-only Graph permissions for user lifecycle, licences, groups, and mailbox settings.
      </p>

      {/* App credentials */}
      {(!cfg.configured || editApp) ? (
        <div style={{ marginBottom: "1.25rem" }}>
          <ol style={{ margin: "0 0 0.75rem 1.1rem", padding: 0, color: "var(--ink-1)", fontSize: "0.82rem", lineHeight: 1.6 }}>
            <li>In Entra: <strong>App registrations → New → Multitenant</strong>. Add Graph <strong>application</strong> permissions (User.ReadWrite.All, Group.ReadWrite.All, Directory.Read.All, MailboxSettings.ReadWrite).</li>
            <li>Add this redirect URI for admin consent:</li>
          </ol>
          <div style={{ display: "flex", gap: "0.4rem", marginBottom: "0.85rem" }}>
            <code style={{ flex: 1, fontFamily: "var(--font-mono)", fontSize: "0.72rem", background: "var(--bg-2)", padding: "0.5rem 0.65rem", borderRadius: "var(--r-xs)", color: "var(--ink-0)", wordBreak: "break-all" }}>{consentRedirect}</code>
            <button onClick={() => navigator.clipboard?.writeText(consentRedirect)} className="ghost-button" style={{ padding: "0.4rem 0.7rem", fontSize: "0.72rem" }}>Copy</button>
          </div>
          <label className="eyebrow" style={{ display: "block", marginBottom: "0.3rem" }}>Application (client) ID</label>
          <input value={clientId} onChange={(e) => setClientId(e.target.value)} placeholder="00000000-0000-0000-0000-000000000000" style={inputStyle} />
          <label className="eyebrow" style={{ display: "block", margin: "0.65rem 0 0.3rem" }}>Client secret</label>
          <input type="password" value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} placeholder="paste the secret value" style={inputStyle} />
          <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.85rem" }}>
            <button onClick={saveApp} disabled={busy || clientId.length < 3 || clientSecret.length < 8} className="primary-button">{busy ? "Saving…" : "Save app credentials"}</button>
            {cfg.configured && <button onClick={() => setEditApp(false)} className="ghost-button">Cancel</button>}
          </div>
        </div>
      ) : (
        <div style={{ marginBottom: "1.25rem", display: "flex", alignItems: "center", gap: "0.85rem" }}>
          <span className="mono" style={{ fontSize: "0.8rem", color: "var(--ink-2)" }}>App: {cfg.client_id ? `${cfg.client_id.slice(0, 8)}…` : "(env)"} · {cfg.source}</span>
          <button onClick={() => setEditApp(true)} className="ghost-button" style={{ padding: "0.3rem 0.7rem", fontSize: "0.72rem" }}>Replace credentials</button>
        </div>
      )}

      {/* Customer tenants */}
      {cfg.configured && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem", flexWrap: "wrap", gap: "0.5rem" }}>
            <h4 style={{ fontFamily: "var(--font-display)", fontSize: "1.05rem", fontWeight: 700 }}>Customer tenants ({customers.length})</h4>
            <button onClick={addViaConsent} disabled={busy} className="primary-button" style={{ padding: "0.45rem 0.85rem", fontSize: "0.82rem" }}>{busy ? "…" : "Add via admin consent →"}</button>
          </div>

          {customers.length === 0 && <p style={{ color: "var(--ink-2)", fontSize: "0.85rem", marginBottom: "0.85rem" }}>No customer tenants linked yet. Use admin consent (recommended) or add a tenant GUID manually below.</p>}
          {customers.map((ct) => (
            <div key={ct.id} style={{ display: "flex", alignItems: "center", gap: "0.75rem", padding: "0.6rem 0", borderTop: "1px solid var(--line)", flexWrap: "wrap" }}>
              <span style={{ flex: 1, minWidth: 180 }}>
                <span style={{ color: "var(--ink-0)", fontSize: "0.88rem" }}>{ct.display_name ?? "(unnamed)"}</span>
                <span className="mono" style={{ display: "block", color: "var(--ink-2)", fontSize: "0.68rem" }}>{ct.ms_tenant_id}</span>
              </span>
              <span style={{ fontSize: "0.7rem", color: ct.status === "linked" ? "var(--ok)" : "var(--warn)" }}>{ct.status}</span>
              <label style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
                <span className="eyebrow" style={{ fontSize: "0.62rem" }}>Halo cust.</span>
                <input defaultValue={ct.halo_customer_id ?? ""} onBlur={(e) => mapHalo(ct.id, e.target.value)} placeholder="id" style={{ ...inputStyle, width: 80, padding: "0.35rem 0.5rem", fontSize: "0.8rem" }} />
              </label>
              <button onClick={() => unlink(ct.id)} className="ghost-button" style={{ padding: "0.25rem 0.6rem", fontSize: "0.7rem" }}>Remove</button>
            </div>
          ))}

          <details style={{ marginTop: "1rem" }}>
            <summary style={{ cursor: "pointer", color: "var(--ink-2)", fontSize: "0.8rem" }}>Add a tenant manually (by Entra tenant GUID)</summary>
            <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.65rem", flexWrap: "wrap" }}>
              <input value={manualMs} onChange={(e) => setManualMs(e.target.value)} placeholder="Entra tenant GUID" style={{ ...inputStyle, flex: "2 1 240px" }} />
              <input value={manualName} onChange={(e) => setManualName(e.target.value)} placeholder="Display name (optional)" style={{ ...inputStyle, flex: "1 1 160px" }} />
              <button onClick={addManual} disabled={busy || manualMs.length < 8} className="ghost-button">Add</button>
            </div>
          </details>
        </div>
      )}

      {error && <p style={{ marginTop: "0.85rem", padding: "0.65rem 0.85rem", background: "var(--danger-soft)", border: "1px solid var(--danger)", borderRadius: "var(--r-sm)", color: "var(--danger)", fontSize: "0.85rem" }}>{error}</p>}
    </article>
  );
}

function IntegrationCard(props: { cfg: { provider: string; name: string; role: string; desc: string; connectable: boolean; comingSoon?: string; apiKeyOnly?: boolean }; live?: Integration; onChange: () => void }) {
  const [showApiKey, setShowApiKey] = useState(false);
  const [apiKey, setApiKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [haloUrl, setHaloUrl] = useState("");
  const [haloClientId, setHaloClientId] = useState("");
  const [haloClientSecret, setHaloClientSecret] = useState("");
  const [showHaloForm, setShowHaloForm] = useState(false);

  const status = props.live?.status;
  const isActive = status === "active";
  const redirectUri = typeof window !== "undefined" ? `${window.location.origin}/oauth/halopsa/callback` : "/oauth/halopsa/callback";

  async function saveAndConnectHalo() {
    setBusy(true); setError(null);
    try {
      // 1. Save the tenant's Halo OAuth app credentials (secret encrypted server-side).
      const save = await fetch("/api/onboarding/integrations/halopsa/config", {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ client_id: haloClientId, client_secret: haloClientSecret, halo_url: haloUrl }),
      });
      const sd = await save.json().catch(() => ({})) as { ok?: boolean; error?: string; detail?: string };
      if (!sd.ok) { setError(sd.detail ?? sd.error ?? "Could not save credentials"); return; }
      // 2. Start the OAuth redirect using the saved credentials.
      const r = await fetch("/api/onboarding/integrations/halopsa/initiate", {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ halo_url: haloUrl, return_to: "/app/integrations" }),
      });
      const data = await r.json().catch(() => ({})) as { authorize_url?: string; error?: string; detail?: string };
      if (data.error) { setError(data.detail ?? data.error); return; }
      if (data.authorize_url) window.location.href = data.authorize_url;
    } catch (err) { setError((err as Error).message); }
    finally { setBusy(false); }
  }
  async function submitItglue() {
    setBusy(true); setError(null);
    try {
      const r = await fetch("/api/onboarding/itglue", { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ api_key: apiKey }) });
      const data = await r.json() as { ok?: boolean; error?: string };
      if (!data.ok) { setError(data.error ?? "Failed"); return; }
      setApiKey(""); setShowApiKey(false); props.onChange();
    } catch (err) { setError((err as Error).message); }
    finally { setBusy(false); }
  }
  async function testHalo() {
    setBusy(true); setError(null);
    try {
      const r = await fetch("/api/onboarding/integrations/halopsa/test", { method: "POST", credentials: "include" });
      const data = await r.json() as { ok?: boolean; record_count?: number; sample_summary?: string; message?: string };
      if (!data.ok) setError(data.message ?? "Test failed");
      else alert(`Halo connectivity OK. ${data.record_count ?? "?"} tickets visible. Sample: ${data.sample_summary ?? "(none)"}`);
    } catch (err) { setError((err as Error).message); }
    finally { setBusy(false); }
  }
  return (
    <article style={{ border: "1px solid var(--line)", borderRadius: "var(--r-md)", background: "var(--bg-1)", padding: "1.5rem 1.75rem" }}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "0.5rem" }}>
        <div>
          <h3 style={{ fontFamily: "var(--font-display)", fontSize: "1.3rem", fontWeight: 700 }}>{props.cfg.name}</h3>
          <p className="mono" style={{ color: "var(--accent)", fontSize: "0.7rem", letterSpacing: "0.08em", textTransform: "uppercase", marginTop: "0.2rem" }}>{props.cfg.role}</p>
        </div>
        <StatusPill status={status ?? (props.cfg.comingSoon ? "coming-soon" : "not-connected")} />
      </header>
      <p style={{ color: "var(--ink-1)", fontSize: "0.9rem", lineHeight: 1.55, marginBottom: "1.25rem" }}>{props.cfg.desc}</p>
      {props.cfg.comingSoon && <p className="eyebrow" style={{ color: "var(--ink-2)" }}>Coming in {props.cfg.comingSoon}</p>}

      {props.cfg.connectable && !isActive && props.cfg.provider === "halopsa" && (
        <div>
          {!showHaloForm ? (
            <button onClick={() => setShowHaloForm(true)} className="primary-button">Connect HaloPSA →</button>
          ) : (
            <div>
              <ol style={{ margin: "0 0 1rem 1.1rem", padding: 0, color: "var(--ink-1)", fontSize: "0.83rem", lineHeight: 1.6 }}>
                <li>In Halo: <strong>Configuration → Integrations → HaloPSA API → View Applications → New</strong>. Authorisation type: <strong>Authorization Code</strong>.</li>
                <li>Paste this <strong>Redirect URI</strong> into the Halo application:</li>
              </ol>
              <div style={{ display: "flex", gap: "0.4rem", marginBottom: "1rem" }}>
                <code style={{ flex: 1, fontFamily: "var(--font-mono)", fontSize: "0.75rem", background: "var(--bg-2)", padding: "0.5rem 0.65rem", borderRadius: "var(--r-xs)", color: "var(--ink-0)", wordBreak: "break-all" }}>{redirectUri}</code>
                <button onClick={() => navigator.clipboard?.writeText(redirectUri)} className="ghost-button" style={{ padding: "0.4rem 0.7rem", fontSize: "0.72rem", whiteSpace: "nowrap" }}>Copy</button>
              </div>
              <p style={{ margin: "0 0 0.4rem", color: "var(--ink-1)", fontSize: "0.83rem" }}>3. Then paste the application's credentials here:</p>

              <label className="eyebrow" style={{ display: "block", marginBottom: "0.3rem" }}>Halo URL</label>
              <input value={haloUrl} onChange={(e) => setHaloUrl(e.target.value)} placeholder="yourmsp.halopsa.com" style={inputStyle} />
              <label className="eyebrow" style={{ display: "block", margin: "0.65rem 0 0.3rem" }}>Client ID</label>
              <input value={haloClientId} onChange={(e) => setHaloClientId(e.target.value)} placeholder="e.g. a1b2c3d4-…" style={inputStyle} />
              <label className="eyebrow" style={{ display: "block", margin: "0.65rem 0 0.3rem" }}>Client Secret</label>
              <input type="password" value={haloClientSecret} onChange={(e) => setHaloClientSecret(e.target.value)} placeholder="paste the secret" style={inputStyle} />

              <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.9rem" }}>
                <button onClick={saveAndConnectHalo} disabled={busy || !haloUrl || haloClientId.length < 3 || haloClientSecret.length < 8} className="primary-button">{busy ? "Connecting…" : "Save & connect →"}</button>
                <button onClick={() => setShowHaloForm(false)} className="ghost-button">Cancel</button>
              </div>
              <p style={{ fontSize: "0.78rem", color: "var(--ink-2)", marginTop: "0.65rem" }}>The secret is encrypted at rest (AES-GCM) and only decrypted to talk to your Halo. You'll be redirected to Halo to authorise, then back here.</p>
            </div>
          )}
        </div>
      )}

      {props.cfg.connectable && !isActive && props.cfg.apiKeyOnly && (
        <div>
          {!showApiKey ? (
            <button onClick={() => setShowApiKey(true)} className="primary-button">Add IT Glue API key →</button>
          ) : (
            <div>
              <label className="eyebrow" style={{ display: "block", marginBottom: "0.4rem" }}>IT Glue API key</label>
              <input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder="ITG.xxx" style={inputStyle} />
              <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.75rem" }}>
                <button onClick={submitItglue} disabled={busy || apiKey.length < 20} className="primary-button">{busy ? "Saving…" : "Save"}</button>
                <button onClick={() => setShowApiKey(false)} className="ghost-button">Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}

      {isActive && (
        <div>
          <div style={{ fontSize: "0.85rem", color: "var(--ink-2)", marginBottom: "0.85rem" }}>
            {props.live?.base_url && <div className="mono" style={{ marginBottom: "0.25rem" }}>{props.live.base_url}</div>}
            <div>Installed {props.live ? new Date(props.live.installed_at).toLocaleString("en-GB") : "—"}</div>
          </div>
          {props.cfg.provider === "halopsa" && <button onClick={testHalo} disabled={busy} className="ghost-button">{busy ? "Testing…" : "Test connection"}</button>}
        </div>
      )}

      {error && <p style={{ marginTop: "0.85rem", padding: "0.65rem 0.85rem", background: "var(--danger-soft)", border: "1px solid var(--danger)", borderRadius: "var(--r-sm)", color: "var(--danger)", fontSize: "0.85rem" }}>{error}</p>}
    </article>
  );
}

function StatusPill({ status }: { status: string }) {
  const palette: Record<string, { color: string; bg: string; label: string }> = {
    active: { color: "var(--ok)", bg: "var(--ok-soft)", label: "Connected" },
    expired: { color: "var(--warn)", bg: "var(--warn-soft)", label: "Token expired" },
    revoked: { color: "var(--danger)", bg: "var(--danger-soft)", label: "Revoked" },
    error: { color: "var(--danger)", bg: "var(--danger-soft)", label: "Error" },
    paused: { color: "var(--ink-2)", bg: "var(--bg-2)", label: "Paused" },
    "not-connected": { color: "var(--ink-2)", bg: "var(--bg-2)", label: "Not connected" },
    "coming-soon": { color: "var(--ink-2)", bg: "var(--bg-2)", label: "Coming soon" },
  };
  const p = palette[status] ?? palette["not-connected"]!;
  return <span style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", padding: "0.25rem 0.55rem", borderRadius: 999, background: p.bg, color: p.color, fontFamily: "var(--font-mono)", fontSize: "0.7rem", letterSpacing: "0.04em" }}><span style={{ width: 6, height: 6, borderRadius: "50%", background: p.color }} />{p.label}</span>;
}

// ─────────────────────────────────────────────────────────────────────
// Skills page
// ─────────────────────────────────────────────────────────────────────

interface SkillRow {
  id: string;
  name: string;
  category: string;
  is_placeholder: boolean;
  enabled: boolean;
  autonomy: string;
  confidence_threshold: number;
  overridden: boolean;
}

function Skills() {
  const [skills, setSkills] = useState<SkillRow[]>([]);
  const [filter, setFilter] = useState("");
  const reload = useCallback(async () => {
    const r = await fetch("/api/skills", { credentials: "include" }).then((r) => r.json()) as { skills: SkillRow[] };
    setSkills(r.skills);
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  async function toggle(name: string, enabled: boolean) {
    await fetch(`/api/skills/${encodeURIComponent(name)}/toggle`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ enabled }) });
    void reload();
  }

  const visible = skills.filter((s) => s.name.toLowerCase().includes(filter.toLowerCase()) || s.category.toLowerCase().includes(filter.toLowerCase()));
  const enabledCount = skills.filter((s) => s.enabled).length;

  return (
    <>
      <PageHeader eyebrow="Skills" title="What Alan can do in your workspace" subtitle={`${enabledCount} enabled of ${skills.length} available. Each skill earns autonomy through consistent success and auto-demotes on rejections.`} />
      <VoiceTest />
      <h2 style={{ fontFamily: "var(--font-display)", fontSize: "1.4rem", fontWeight: 700, margin: "2.5rem 0 1rem" }}>All skills</h2>
      <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter by name or category…" style={{ ...inputStyle, maxWidth: 480, marginBottom: "1.5rem" }} />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: "0.75rem" }}>
        {visible.map((s) => (
          <div key={s.id} style={{ padding: "1rem 1.15rem", border: "1px solid var(--line)", borderRadius: "var(--r-sm)", background: s.enabled ? "var(--bg-1)" : "var(--bg-2)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "0.4rem" }}>
              <div className="mono" style={{ color: "var(--ink-0)", fontWeight: 500 }}>{s.name}</div>
              <Toggle checked={s.enabled} onChange={(v) => toggle(s.name, v)} />
            </div>
            <div style={{ display: "flex", gap: "0.4rem", fontFamily: "var(--font-mono)", fontSize: "0.65rem", color: "var(--ink-2)", flexWrap: "wrap" }}>
              <span style={{ padding: "0.15rem 0.4rem", border: "1px solid var(--line-2)", borderRadius: 2 }}>{s.category}</span>
              <span style={{ padding: "0.15rem 0.4rem", border: "1px solid var(--line-2)", borderRadius: 2 }}>{s.autonomy}</span>
              <span style={{ padding: "0.15rem 0.4rem", border: "1px solid var(--line-2)", borderRadius: 2 }}>conf {s.confidence_threshold}</span>
              {s.is_placeholder && <span style={{ padding: "0.15rem 0.4rem", border: "1px solid var(--warn)", color: "var(--warn)", borderRadius: 2 }}>placeholder</span>}
              {s.overridden && <span style={{ padding: "0.15rem 0.4rem", border: "1px solid var(--accent-line)", color: "var(--accent)", borderRadius: 2 }}>overridden</span>}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button onClick={() => onChange(!checked)} style={{ position: "relative", width: 36, height: 20, border: "none", borderRadius: 999, background: checked ? "var(--accent)" : "var(--bg-3)", cursor: "pointer", padding: 0 }}>
      <span style={{ position: "absolute", top: 2, left: checked ? 18 : 2, width: 16, height: 16, borderRadius: "50%", background: checked ? "var(--accent-ink)" : "var(--ink-1)", transition: "left 0.12s" }} />
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Voice test panel — calibrate Alan's tone before going live
// ─────────────────────────────────────────────────────────────────────

interface VoiceConfigShape {
  brand_name: string;
  sign_off: string;
  register: "uk-formal" | "uk-casual";
  contractions: boolean;
  acknowledge_jokes: boolean;
  support_email?: string;
  calendly_url?: string;
}

interface VoiceSampleShape {
  id: string;
  scenario: string;
  ticket: { id: number; summary: string; details: string; customer_name: string; user_name: string };
  matched_skill: string | null;
}

interface VoicePreview {
  sample_id: string;
  draft: string;
  cache: boolean;
}

function VoiceTest() {
  const [voice, setVoice] = useState<VoiceConfigShape | null>(null);
  const [samples, setSamples] = useState<VoiceSampleShape[]>([]);
  const [drafts, setDrafts] = useState<Record<string, VoicePreview>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  const reload = useCallback(async () => {
    const [v, s] = await Promise.all([
      fetch("/api/voice", { credentials: "include" }).then((r) => r.json()) as Promise<{ voice: VoiceConfigShape }>,
      fetch("/api/voice/samples", { credentials: "include" }).then((r) => r.json()) as Promise<{ samples: VoiceSampleShape[] }>,
    ]);
    setVoice(v.voice);
    setSamples(s.samples);
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  async function saveVoice() {
    if (!voice) return;
    setSaving(true);
    try {
      await fetch("/api/voice", {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(voice),
      });
      setSavedFlash(true);
      setDrafts({}); // voice changed → invalidate previews
      setTimeout(() => setSavedFlash(false), 2000);
    } finally { setSaving(false); }
  }

  async function preview(sample_id: string) {
    setBusy(sample_id);
    try {
      const r = await fetch("/api/voice/preview", {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sample_id, voice_overrides: voice }),
      }).then((r) => r.json()) as VoicePreview;
      setDrafts((d) => ({ ...d, [sample_id]: r }));
    } finally { setBusy(null); }
  }

  if (!voice) return null;

  return (
    <section style={{ border: "1px solid var(--accent-line)", background: "linear-gradient(180deg, var(--bg-1), var(--bg-0))", borderRadius: "var(--r-md)", padding: "1.5rem 1.75rem", marginTop: "0.5rem" }}>
      <header style={{ marginBottom: "1.25rem" }}>
        <p className="eyebrow-accent" style={{ marginBottom: "0.3rem" }}>Voice test · onboarding</p>
        <h2 style={{ fontFamily: "var(--font-display)", fontSize: "1.4rem", fontWeight: 700, marginBottom: "0.4rem" }}>Set Alan's tone before going live</h2>
        <p style={{ color: "var(--ink-1)", fontSize: "0.9rem", maxWidth: "70ch", lineHeight: 1.55 }}>
          Alan signs as your brand, never as himself. Five sample tickets below let you preview how he writes with the current voice config. Adjust and re-run until it sounds like a member of your team.
        </p>
      </header>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.85rem 1.25rem", marginBottom: "1.25rem", maxWidth: 720 }}>
        <label>
          <span className="eyebrow" style={{ display: "block", marginBottom: "0.3rem" }}>Brand name (sent as)</span>
          <input value={voice.brand_name} onChange={(e) => setVoice({ ...voice, brand_name: e.target.value })} style={inputStyle} />
        </label>
        <label>
          <span className="eyebrow" style={{ display: "block", marginBottom: "0.3rem" }}>Sign-off</span>
          <input value={voice.sign_off} onChange={(e) => setVoice({ ...voice, sign_off: e.target.value })} style={inputStyle} />
        </label>
        <label>
          <span className="eyebrow" style={{ display: "block", marginBottom: "0.3rem" }}>Register</span>
          <select value={voice.register} onChange={(e) => setVoice({ ...voice, register: e.target.value as VoiceConfigShape["register"] })} style={inputStyle}>
            <option value="uk-formal">UK formal</option>
            <option value="uk-casual">UK casual</option>
          </select>
        </label>
        <label>
          <span className="eyebrow" style={{ display: "block", marginBottom: "0.3rem" }}>Calendly URL (for remote-session offers)</span>
          <input value={voice.calendly_url ?? ""} onChange={(e) => setVoice({ ...voice, calendly_url: e.target.value })} placeholder="https://calendly.com/your-team/quick" style={inputStyle} />
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: "0.65rem", marginTop: "0.55rem" }}>
          <input type="checkbox" checked={voice.contractions} onChange={(e) => setVoice({ ...voice, contractions: e.target.checked })} />
          <span style={{ fontSize: "0.88rem", color: "var(--ink-1)" }}>Allow contractions (we'll, you're)</span>
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: "0.65rem", marginTop: "0.55rem" }}>
          <input type="checkbox" checked={voice.acknowledge_jokes} onChange={(e) => setVoice({ ...voice, acknowledge_jokes: e.target.checked })} />
          <span style={{ fontSize: "0.88rem", color: "var(--ink-1)" }}>Acknowledge user jokes (briefly)</span>
        </label>
      </div>

      <div style={{ display: "flex", gap: "0.6rem", marginBottom: "1.75rem", alignItems: "center" }}>
        <button onClick={saveVoice} disabled={saving} className="primary-button">{saving ? "Saving…" : "Save voice"}</button>
        {savedFlash && <span className="mono" style={{ fontSize: "0.78rem", color: "var(--ok)" }}>✓ Saved · previews cleared</span>}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(330px, 1fr))", gap: "0.85rem" }}>
        {samples.map((s) => {
          const d = drafts[s.id];
          return (
            <div key={s.id} style={{ padding: "1rem 1.15rem", border: "1px solid var(--line)", borderRadius: "var(--r-sm)", background: "var(--bg-1)", display: "flex", flexDirection: "column", gap: "0.55rem" }}>
              <div className="eyebrow-accent" style={{ fontSize: "0.65rem" }}>{s.id}</div>
              <div style={{ fontSize: "0.82rem", color: "var(--ink-1)", lineHeight: 1.5, marginBottom: "0.2rem" }}>{s.scenario}</div>
              <div style={{ fontSize: "0.75rem", color: "var(--ink-2)", fontStyle: "italic", borderLeft: "2px solid var(--line-2)", paddingLeft: "0.65rem", marginBottom: "0.55rem" }}>"{s.ticket.summary}" — {s.ticket.user_name} @ {s.ticket.customer_name}</div>
              {d ? (
                <div>
                  <pre style={{ fontFamily: "var(--font-mono)", fontSize: "0.72rem", color: "var(--ink-1)", whiteSpace: "pre-wrap", background: "var(--bg-2)", padding: "0.75rem", borderRadius: "var(--r-xs)", margin: 0, maxHeight: 360, overflow: "auto", lineHeight: 1.5 }}>{d.draft.trim()}</pre>
                  <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.55rem", alignItems: "center" }}>
                    <button onClick={() => preview(s.id)} disabled={busy === s.id} className="ghost-button" style={{ padding: "0.35rem 0.7rem", fontSize: "0.72rem" }}>{busy === s.id ? "Running…" : "Re-run"}</button>
                    {d.cache && <span className="mono" style={{ fontSize: "0.65rem", color: "var(--ink-2)" }}>cached</span>}
                  </div>
                </div>
              ) : (
                <button onClick={() => preview(s.id)} disabled={busy === s.id} className="primary-button" style={{ alignSelf: "flex-start", padding: "0.45rem 0.85rem", fontSize: "0.8rem" }}>{busy === s.id ? "Running…" : "Preview draft →"}</button>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Settings page
// ─────────────────────────────────────────────────────────────────────

function Settings() {
  const [tenant, setTenant] = useState<TenantResponse["tenant"] | null>(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);

  const reload = useCallback(async () => {
    const r = await fetch("/api/settings/tenant", { credentials: "include" }).then((r) => r.json()) as TenantResponse;
    setTenant(r.tenant);
    setName(r.tenant.name);
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  async function saveWorkspace() {
    setBusy(true); setFlash(null);
    try {
      const r = await fetch("/api/settings/workspace", { method: "PATCH", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ name }) });
      if (r.ok) setFlash("Saved");
      void reload();
    } finally { setBusy(false); }
  }
  async function setDryRun(v: boolean) {
    await fetch("/api/settings/dry-run", { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ dry_run: v }) });
    void reload();
  }
  async function setKillswitch(v: boolean) {
    if (v && !confirm("Activate kill switch? This immediately stops all polling and queues for this workspace.")) return;
    await fetch("/api/settings/killswitch", { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ active: v }) });
    void reload();
  }

  if (!tenant) return <FullPageLoader label="Loading settings…" />;

  return (
    <>
      <PageHeader eyebrow="Settings" title="Workspace settings" subtitle="Identity, modes, data region. Every change is logged to the audit trail." />
      <Card title="Workspace identity">
        <label className="eyebrow" style={{ display: "block", marginBottom: "0.4rem" }}>Workspace name</label>
        <input value={name} onChange={(e) => setName(e.target.value)} style={inputStyle} />
        <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.75rem" }}>
          <button onClick={saveWorkspace} disabled={busy || name === tenant.name} className="primary-button">{busy ? "Saving…" : "Save"}</button>
          {flash && <span style={{ alignSelf: "center", color: "var(--ok)", fontSize: "0.85rem" }}>{flash}</span>}
        </div>
        <div className="mono" style={{ color: "var(--ink-2)", fontSize: "0.78rem", marginTop: "1rem" }}>
          slug: {tenant.slug} · plan: {tenant.plan} · region: {tenant.data_region.toUpperCase()}
        </div>
      </Card>

      <Card title="Operating mode" subtitle="Dry-run = Alan reads + drafts but never writes. Kill switch = halt everything immediately.">
        <Row label="Dry-run mode" detail={tenant.dry_run ? "Alan will not modify your Halo. Safe for the 14-day pilot." : "Alan can perform approved actions on your Halo."}>
          <Toggle checked={tenant.dry_run} onChange={setDryRun} />
        </Row>
        <Row label="Kill switch" detail={tenant.killswitch ? "ALL POLLING + EXECUTION STOPPED." : "Hit this if anything looks wrong. Reversible."} danger={tenant.killswitch}>
          <Toggle checked={tenant.killswitch} onChange={setKillswitch} />
        </Row>
      </Card>

      <Card title="Plan + seats" subtitle="Current plan and licensed-seat count. Billing wiring lands Phase 4.">
        <Row label="Plan" detail={`${tenant.plan} · ${tenant.seats_used} of ${tenant.seats_licensed} seats used`}>
          <span className="mono" style={{ color: "var(--ink-2)", fontSize: "0.85rem" }}>£50 / tech / month when live</span>
        </Row>
      </Card>
    </>
  );
}

function Row({ label, detail, danger, children }: { label: string; detail: string; danger?: boolean; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0.85rem 0", borderTop: "1px solid var(--line)", gap: "1.5rem" }}>
      <div>
        <div style={{ color: "var(--ink-0)", fontWeight: 500, marginBottom: "0.2rem" }}>{label}</div>
        <div style={{ color: danger ? "var(--danger)" : "var(--ink-2)", fontSize: "0.85rem" }}>{detail}</div>
      </div>
      {children}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Try Alan — paste a ticket, see the full triage decision
// ─────────────────────────────────────────────────────────────────────

interface TriageDecisionView {
  skill: string | null;
  confidence: number;
  priority: "critical" | "high" | "medium" | "low";
  priority_numeric?: 1 | 2 | 3 | 4;
  impact_score?: number;
  urgency_score?: number;
  priority_trace?: string[];
  category: string;
  customer_impact: string;
  recommended_action: "auto_route_ai" | "needs_human" | "needs_info" | "enrich_only";
  summary: string;
  reasoning: string;
  unhandled_scope?: string[];
  info_request_draft?: string;
  tier_used?: "cache" | "embedding" | "haiku" | "sonnet";
  model: string;
  tokens_in: number;
  tokens_out: number;
}

const PRIORITY_PALETTE: Record<TriageDecisionView["priority"], { color: string; bg: string }> = {
  critical: { color: "var(--danger)", bg: "var(--danger-soft)" },
  high: { color: "var(--warn)", bg: "var(--warn-soft)" },
  medium: { color: "var(--info)", bg: "var(--info-soft)" },
  low: { color: "var(--ok)", bg: "var(--ok-soft)" },
};

function TryAlan() {
  const [summary, setSummary] = useState("Outlook keeps crashing on launch");
  const [details, setDetails] = useState("Hi — Outlook crashes the moment I open it. Tried a restart, same result. I have meetings booked through Outlook today and need this fixed. Thanks. — Tom");
  const [customer, setCustomer] = useState("");
  const [vip, setVip] = useState(false);
  const [sla, setSla] = useState("");
  const [busy, setBusy] = useState(false);
  const [decision, setDecision] = useState<TriageDecisionView | null>(null);
  const [error, setError] = useState<{ status: number; message: string } | null>(null);
  const [latencyMs, setLatencyMs] = useState<number | null>(null);

  async function run() {
    setBusy(true);
    setError(null);
    setDecision(null);
    const t0 = performance.now();
    try {
      const res = await fetch("/api/triage/preview", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          summary,
          details,
          customer_name: customer || undefined,
          customer_vip: vip || undefined,
          customer_sla_tier: sla || undefined,
        }),
      });
      const data = (await res.json()) as { decision?: TriageDecisionView; error?: string; detail?: string };
      if (!res.ok) {
        setError({ status: res.status, message: data.detail ?? data.error ?? `HTTP ${res.status}` });
        return;
      }
      setDecision(data.decision ?? null);
    } catch (err) {
      setError({ status: 0, message: (err as Error).message });
    } finally {
      setBusy(false);
      setLatencyMs(performance.now() - t0);
    }
  }

  return (
    <>
      <PageHeader
        eyebrow="Triage preview"
        title="Try Alan on a real ticket"
        subtitle="Paste a ticket's subject + body and see Alan's full triage decision in one round-trip. Skill match, confidence, ITIL 4 priority with trace, recommended action, drafted info-request email when applicable. No Halo write happens — this is read-only."
      />
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1.2fr)", gap: "1.5rem", alignItems: "flex-start" }}>
        <section style={{ padding: "1.5rem 1.75rem", border: "1px solid var(--line)", background: "var(--bg-1)", borderRadius: "var(--r-md)" }}>
          <h2 style={{ fontFamily: "var(--font-display)", fontSize: "1.1rem", fontWeight: 700, marginBottom: "1rem" }}>Ticket input</h2>
          <label style={{ display: "block", marginBottom: "0.85rem" }}>
            <span className="eyebrow" style={{ display: "block", marginBottom: "0.3rem" }}>Subject</span>
            <input value={summary} onChange={(e) => setSummary(e.target.value)} style={inputStyle} />
          </label>
          <label style={{ display: "block", marginBottom: "0.85rem" }}>
            <span className="eyebrow" style={{ display: "block", marginBottom: "0.3rem" }}>Body</span>
            <textarea value={details} onChange={(e) => setDetails(e.target.value)} rows={8} style={{ ...inputStyle, fontFamily: "inherit", resize: "vertical" }} />
          </label>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.85rem", marginBottom: "0.85rem" }}>
            <label>
              <span className="eyebrow" style={{ display: "block", marginBottom: "0.3rem" }}>Customer (optional)</span>
              <input value={customer} onChange={(e) => setCustomer(e.target.value)} placeholder="Acme Ltd" style={inputStyle} />
            </label>
            <label>
              <span className="eyebrow" style={{ display: "block", marginBottom: "0.3rem" }}>SLA tier (optional)</span>
              <select value={sla} onChange={(e) => setSla(e.target.value)} style={inputStyle}>
                <option value="">—</option>
                <option value="standard">standard</option>
                <option value="premium">premium</option>
                <option value="enterprise">enterprise</option>
                <option value="24x7">24x7</option>
              </select>
            </label>
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: "0.55rem", marginBottom: "1rem" }}>
            <input type="checkbox" checked={vip} onChange={(e) => setVip(e.target.checked)} />
            <span style={{ fontSize: "0.88rem", color: "var(--ink-1)" }}>Caller is a VIP</span>
          </label>
          <button onClick={run} disabled={busy || (!summary && !details)} className="primary-button">
            {busy ? "Triaging…" : "Triage this →"}
          </button>
        </section>

        <section>
          {!decision && !error && (
            <div style={{ padding: "2rem 1.75rem", border: "1px dashed var(--line-2)", borderRadius: "var(--r-md)", color: "var(--ink-2)", textAlign: "center", fontSize: "0.92rem" }}>
              Triage output will appear here.
            </div>
          )}
          {error && (
            <div style={{ padding: "1.25rem 1.5rem", border: "1px solid var(--danger)", background: "var(--danger-soft)", borderRadius: "var(--r-md)" }}>
              <div className="mono" style={{ color: "var(--danger)", fontSize: "0.7rem", letterSpacing: "0.08em", marginBottom: "0.45rem" }}>HTTP {error.status}</div>
              <div style={{ color: "var(--ink-0)", fontSize: "0.92rem", lineHeight: 1.5 }}>{error.message}</div>
            </div>
          )}
          {decision && <TriageDecisionCard decision={decision} latencyMs={latencyMs} />}
        </section>
      </div>
    </>
  );
}

function TriageDecisionCard({ decision, latencyMs }: { decision: TriageDecisionView; latencyMs: number | null }) {
  const palette = PRIORITY_PALETTE[decision.priority];
  return (
    <div style={{ padding: "1.5rem 1.75rem", border: "1px solid var(--accent-line)", background: "linear-gradient(180deg, var(--bg-1), var(--bg-0))", borderRadius: "var(--r-md)" }}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "1.25rem", gap: "1rem", flexWrap: "wrap" }}>
        <div>
          <p className="eyebrow-accent" style={{ marginBottom: "0.3rem" }}>Decision</p>
          <h2 style={{ fontFamily: "var(--font-display)", fontSize: "1.3rem", fontWeight: 700 }}>
            {decision.skill ?? "No skill matched"}
          </h2>
        </div>
        <span style={{ padding: "0.3rem 0.7rem", borderRadius: 999, background: palette.bg, color: palette.color, fontFamily: "var(--font-mono)", fontSize: "0.72rem", letterSpacing: "0.04em", textTransform: "uppercase", fontWeight: 700 }}>
          P{decision.priority_numeric ?? "?"} · {decision.priority}
        </span>
      </header>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: "0.85rem", marginBottom: "1.25rem" }}>
        <Stat label="Confidence" value={`${decision.confidence}%`} />
        <Stat label="Impact" value={String(decision.impact_score ?? "—")} />
        <Stat label="Urgency" value={String(decision.urgency_score ?? "—")} />
        <Stat label="Tier" value={decision.tier_used ?? "—"} />
      </div>

      <div style={{ marginBottom: "1rem" }}>
        <p className="eyebrow" style={{ marginBottom: "0.4rem" }}>Recommended action</p>
        <div className="mono" style={{ color: "var(--ink-0)", fontSize: "0.85rem", padding: "0.45rem 0.7rem", border: "1px solid var(--line-2)", borderRadius: "var(--r-xs)", display: "inline-block" }}>
          {decision.recommended_action}
        </div>
      </div>

      <div style={{ marginBottom: "1rem" }}>
        <p className="eyebrow" style={{ marginBottom: "0.4rem" }}>Category</p>
        <div className="mono" style={{ color: "var(--ink-1)", fontSize: "0.85rem" }}>{decision.category}</div>
      </div>

      <div style={{ marginBottom: "1rem" }}>
        <p className="eyebrow" style={{ marginBottom: "0.4rem" }}>Customer impact</p>
        <div style={{ color: "var(--ink-0)", fontSize: "0.9rem", lineHeight: 1.55 }}>{decision.customer_impact}</div>
      </div>

      {decision.priority_trace && decision.priority_trace.length > 0 && (
        <div style={{ marginBottom: "1rem" }}>
          <p className="eyebrow" style={{ marginBottom: "0.4rem" }}>Priority trace</p>
          <ul style={{ margin: 0, paddingLeft: "1.1rem", color: "var(--ink-1)", fontSize: "0.85rem", lineHeight: 1.55, fontFamily: "var(--font-mono)" }}>
            {decision.priority_trace.map((t, i) => <li key={i}>{t}</li>)}
          </ul>
        </div>
      )}

      <div style={{ marginBottom: "1rem" }}>
        <p className="eyebrow" style={{ marginBottom: "0.4rem" }}>Reasoning</p>
        <div style={{ color: "var(--ink-1)", fontSize: "0.88rem", lineHeight: 1.55, fontStyle: "italic" }}>{decision.reasoning}</div>
      </div>

      {decision.info_request_draft && (
        <div style={{ marginBottom: "1rem" }}>
          <p className="eyebrow" style={{ marginBottom: "0.4rem" }}>Drafted info-request email</p>
          <pre style={{ fontFamily: "var(--font-mono)", fontSize: "0.78rem", color: "var(--ink-1)", whiteSpace: "pre-wrap", background: "var(--bg-2)", padding: "0.85rem", borderRadius: "var(--r-xs)", margin: 0, lineHeight: 1.55 }}>{decision.info_request_draft}</pre>
        </div>
      )}

      {decision.unhandled_scope && decision.unhandled_scope.length > 0 && (
        <div style={{ marginBottom: "1rem" }}>
          <p className="eyebrow" style={{ marginBottom: "0.4rem" }}>Out-of-scope items flagged</p>
          <ul style={{ margin: 0, paddingLeft: "1.1rem", color: "var(--ink-1)", fontSize: "0.85rem" }}>
            {decision.unhandled_scope.map((s, i) => <li key={i}>{s}</li>)}
          </ul>
        </div>
      )}

      <footer style={{ paddingTop: "0.85rem", borderTop: "1px dashed var(--line-2)", display: "flex", gap: "1rem", flexWrap: "wrap", fontFamily: "var(--font-mono)", fontSize: "0.7rem", color: "var(--ink-2)" }}>
        <span>{decision.model}</span>
        <span>{decision.tokens_in} in</span>
        <span>{decision.tokens_out} out</span>
        {latencyMs !== null && <span>{Math.round(latencyMs)}ms round-trip</span>}
      </footer>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Security Centre — live readiness of the whole system
// ─────────────────────────────────────────────────────────────────────

interface DeepHealth {
  overall: "ready" | "degraded" | "blocked";
  subsystems: Record<string, { state: "ready" | "degraded" | "blocked"; detail?: string; data?: Record<string, unknown> }>;
  env: string;
  ts: string;
}

const STATE_PALETTE = {
  ready: { color: "var(--ok)", bg: "var(--ok-soft)", label: "Ready" },
  degraded: { color: "var(--warn)", bg: "var(--warn-soft)", label: "Degraded" },
  blocked: { color: "var(--danger)", bg: "var(--danger-soft)", label: "Blocked" },
} as const;

function SecurityCentre() {
  const [health, setHealth] = useState<DeepHealth | null>(null);
  const [tenant, setTenant] = useState<TenantResponse["tenant"] | null>(null);
  const [floor, setFloor] = useState<DryRunFloor | null>(null);
  const [verify, setVerify] = useState<VerifyResult | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [reloading, setReloading] = useState(false);

  const reload = useCallback(async () => {
    setReloading(true);
    try {
      const [h, t, f] = await Promise.all([
        fetch("/health/deep").then((r) => r.json()) as Promise<DeepHealth>,
        fetch("/api/settings/tenant", { credentials: "include" }).then((r) => r.json()) as Promise<TenantResponse>,
        fetch("/api/settings/dry-run-floor", { credentials: "include" }).then((r) => r.json()) as Promise<DryRunFloor>,
      ]);
      setHealth(h);
      setTenant(t.tenant);
      setFloor(f);
    } finally {
      setReloading(false);
    }
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  async function runVerify() {
    setVerifying(true);
    try {
      const r = await fetch("/api/settings/audit/verify", { credentials: "include" }).then((r) => r.json()) as VerifyResult;
      setVerify(r);
    } finally { setVerifying(false); }
  }

  return (
    <>
      <PageHeader
        eyebrow="Security centre"
        title="Everything that needs to be true for Alan to be safe"
        subtitle="Live readiness across encryption, audit, AI, integrations, and tenant safety controls. If anything here is red, that's where to look first."
        action={
          <button onClick={reload} disabled={reloading} className="ghost-button">
            {reloading ? "Refreshing…" : "Refresh"}
          </button>
        }
      />

      {/* Overall + tenant state */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "1rem", marginBottom: "2rem" }}>
        <Stat
          label="Overall"
          value={health ? STATE_PALETTE[health.overall].label : "—"}
          accent={health ? STATE_PALETTE[health.overall].color : undefined}
        />
        <Stat
          label="Mode"
          value={tenant?.killswitch ? "Kill switch" : tenant?.dry_run ? "Dry-run" : "Live"}
          accent={tenant?.killswitch ? "var(--danger)" : tenant?.dry_run ? "var(--warn)" : "var(--ok)"}
          sub={tenant?.dry_run && floor && !floor.unlockable_now ? `${floor.days_remaining}d remaining in floor` : undefined}
        />
        <Stat label="Region" value={tenant?.data_region?.toUpperCase() ?? "—"} />
        <Stat label="Plan" value={tenant?.plan ?? "—"} />
      </div>

      {/* Subsystem grid */}
      {health && (
        <section style={{ marginBottom: "2rem" }}>
          <h2 style={{ fontFamily: "var(--font-display)", fontSize: "1.2rem", fontWeight: 700, marginBottom: "0.85rem" }}>Subsystems</h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "0.85rem" }}>
            {Object.entries(health.subsystems).map(([name, s]) => {
              const palette = STATE_PALETTE[s.state];
              return (
                <div key={name} style={{ padding: "1.1rem 1.3rem", border: "1px solid var(--line)", background: "var(--bg-1)", borderRadius: "var(--r-md)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "0.5rem" }}>
                    <div className="mono" style={{ color: "var(--ink-0)", fontWeight: 500, fontSize: "0.92rem" }}>{name}</div>
                    <span style={{ padding: "0.15rem 0.5rem", borderRadius: 999, background: palette.bg, color: palette.color, fontFamily: "var(--font-mono)", fontSize: "0.65rem", letterSpacing: "0.06em", textTransform: "uppercase", fontWeight: 700 }}>
                      {palette.label}
                    </span>
                  </div>
                  {s.detail && <div style={{ color: "var(--ink-1)", fontSize: "0.83rem", lineHeight: 1.5, marginBottom: "0.4rem" }}>{s.detail}</div>}
                  {s.data && (
                    <div className="mono" style={{ fontSize: "0.7rem", color: "var(--ink-2)", display: "flex", gap: "0.85rem", flexWrap: "wrap" }}>
                      {Object.entries(s.data).map(([k, v]) => (
                        <span key={k}>{k}: <span style={{ color: "var(--ink-0)" }}>{String(v)}</span></span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Audit chain integrity */}
      <section style={{ marginBottom: "2rem", padding: "1.25rem 1.5rem", border: "1px solid var(--line)", borderRadius: "var(--r-md)", background: "var(--bg-1)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.6rem", flexWrap: "wrap", gap: "1rem" }}>
          <div>
            <h2 style={{ fontFamily: "var(--font-display)", fontSize: "1.2rem", fontWeight: 700 }}>Audit chain integrity</h2>
            <p style={{ color: "var(--ink-1)", fontSize: "0.88rem", marginTop: "0.25rem" }}>
              Hash-chained. Tampering with any row's payload breaks the chain at that index.
            </p>
          </div>
          <button onClick={runVerify} disabled={verifying} className="primary-button">
            {verifying ? "Verifying…" : "Verify now →"}
          </button>
        </div>
        {verify && (
          <div style={{ marginTop: "0.85rem", padding: "0.75rem 1rem", borderRadius: "var(--r-sm)", background: verify.valid ? "var(--ok-soft)" : "var(--danger-soft)", border: `1px solid ${verify.valid ? "var(--ok)" : "var(--danger)"}`, fontSize: "0.88rem" }}>
            <strong style={{ color: verify.valid ? "var(--ok)" : "var(--danger)" }}>
              {verify.valid ? "Intact" : "Broken"}
            </strong>{" "}
            · {verify.total} row{verify.total === 1 ? "" : "s"} checked at {new Date(verify.checked_at).toLocaleString("en-GB")}
          </div>
        )}
      </section>

      <p className="mono" style={{ color: "var(--ink-2)", fontSize: "0.78rem", textAlign: "center" }}>
        Public spec sheet → <Link to="/security" style={{ color: "var(--accent)" }}>/security</Link> ·
        DPA → <Link to="/legal/dpa" style={{ color: "var(--accent)" }}>/legal/dpa</Link>
      </p>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Sentiment page
// ─────────────────────────────────────────────────────────────────────

const SENTIMENT_COLOUR: Record<string, string> = {
  calm: "var(--ok)", neutral: "var(--info)", frustrated: "var(--warn)",
  panicked: "var(--danger)", angry: "var(--danger)",
};

interface SentimentOverview {
  window_days: number;
  total_samples: number;
  tenant_mean_score: number;
  tenant_mean_label: string;
  worst_customers: Array<{ halo_customer_id: number; mean_score: number; mean_label: string; sample_count: number }>;
}

function SentimentPage() {
  const [data, setData] = useState<SentimentOverview | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    void fetch("/api/sentiment/overview", { credentials: "include" })
      .then((r) => r.json()).then((d: SentimentOverview) => setData(d)).finally(() => setLoading(false));
  }, []);

  return (
    <>
      <PageHeader eyebrow="Intelligence" title="Customer sentiment" subtitle="Tone captured at triage time, aggregated per customer. Rising scores = churn risk. Drives the CSM-alert engine." />
      {loading && <p className="eyebrow">Loading…</p>}
      {data && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "1rem", marginBottom: "2rem" }}>
            <Stat label="Mean tone" value={data.tenant_mean_label} accent={SENTIMENT_COLOUR[data.tenant_mean_label]} sub={`${data.tenant_mean_score}/4 across ${data.total_samples} tickets`} />
            <Stat label="Window" value={`${data.window_days}d`} />
            <Stat label="At-risk customers" value={String(data.worst_customers.filter((c) => c.mean_score >= 2.5).length)} accent={data.worst_customers.some((c) => c.mean_score >= 2.5) ? "var(--warn)" : "var(--ok)"} />
            <Stat label="Samples" value={String(data.total_samples)} />
          </div>
          <Card title="Customers by frustration" subtitle="Ranked worst-first. Only customers with 3+ triaged tickets in the window appear.">
            {data.worst_customers.length === 0 && <p style={{ color: "var(--ink-2)" }}>No sentiment data yet. It populates as tickets are triaged.</p>}
            {data.worst_customers.map((c) => (
              <div key={c.halo_customer_id} style={{ display: "flex", alignItems: "center", gap: "1rem", padding: "0.6rem 0", borderTop: "1px solid var(--line)" }}>
                <span className="mono" style={{ color: "var(--ink-2)", width: 90 }}>#{c.halo_customer_id}</span>
                <div style={{ flex: 1, height: 8, background: "var(--bg-3)", borderRadius: 999, overflow: "hidden" }}>
                  <div style={{ width: `${(c.mean_score / 4) * 100}%`, height: "100%", background: SENTIMENT_COLOUR[c.mean_label] }} />
                </div>
                <span style={{ color: SENTIMENT_COLOUR[c.mean_label], fontWeight: 600, width: 90, fontSize: "0.85rem" }}>{c.mean_label}</span>
                <span className="mono" style={{ color: "var(--ink-2)", fontSize: "0.78rem", width: 70 }}>{c.sample_count} tkts</span>
              </div>
            ))}
          </Card>
        </>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────
// QA page
// ─────────────────────────────────────────────────────────────────────

interface QaSummaryData {
  window_days: number; total: number; passed: number; failed: number;
  pass_rate: number; reopens: number; unusual_fixes: number; mean_score: number;
}
interface QaFailedRow { id: string; halo_ticket_id: number | null; score: number; flags: string; is_reopen: boolean; unusual_fix: boolean; created_at: number }

function QaPage() {
  const [summary, setSummary] = useState<QaSummaryData | null>(null);
  const [failed, setFailed] = useState<QaFailedRow[]>([]);
  useEffect(() => {
    void fetch("/api/qa/summary", { credentials: "include" }).then((r) => r.json()).then(setSummary);
    void fetch("/api/qa/failed", { credentials: "include" }).then((r) => r.json()).then((d: { audits: QaFailedRow[] }) => setFailed(d.audits));
  }, []);
  return (
    <>
      <PageHeader eyebrow="Intelligence" title="Quality assurance" subtitle="Automated closure audits: notes, time logged, root cause, steps. Reopen + unusual-fix detection. Built on the tamper-evident audit trail." />
      {summary && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "1rem", marginBottom: "2rem" }}>
          <Stat label="Pass rate" value={`${Math.round(summary.pass_rate * 100)}%`} accent={summary.pass_rate >= 0.8 ? "var(--ok)" : "var(--warn)"} sub={`${summary.passed}/${summary.total} audits`} />
          <Stat label="Mean score" value={String(summary.mean_score)} />
          <Stat label="Reopens" value={String(summary.reopens)} accent={summary.reopens > 0 ? "var(--warn)" : undefined} />
          <Stat label="Unusual fixes" value={String(summary.unusual_fixes)} accent={summary.unusual_fixes > 0 ? "var(--warn)" : undefined} />
        </div>
      )}
      <Card title="Failed audits" subtitle="Closures that missed the bar. Each needs documentation cleanup or a peer review.">
        {failed.length === 0 && <p style={{ color: "var(--ink-2)" }}>No failed audits. Either nothing's been audited yet, or every closure passed.</p>}
        {failed.map((f) => {
          const flags = JSON.parse(f.flags || "[]") as string[];
          return (
            <div key={f.id} style={{ padding: "0.85rem 0", borderTop: "1px solid var(--line)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.35rem" }}>
                <span className="mono" style={{ color: "var(--ink-0)" }}>{f.halo_ticket_id ? `#${f.halo_ticket_id}` : f.id.slice(0, 12)}</span>
                <span style={{ display: "flex", gap: "0.4rem" }}>
                  <span className="mono" style={{ color: f.score >= 70 ? "var(--warn)" : "var(--danger)", fontSize: "0.8rem" }}>score {f.score}</span>
                  {f.is_reopen && <span style={{ fontSize: "0.7rem", color: "var(--warn)", border: "1px solid var(--warn)", borderRadius: 2, padding: "0 0.3rem" }}>reopen</span>}
                  {f.unusual_fix && <span style={{ fontSize: "0.7rem", color: "var(--danger)", border: "1px solid var(--danger)", borderRadius: 2, padding: "0 0.3rem" }}>unusual</span>}
                </span>
              </div>
              <ul style={{ margin: 0, paddingLeft: "1.1rem", color: "var(--ink-1)", fontSize: "0.83rem" }}>
                {flags.map((fl, i) => <li key={i}>{fl}</li>)}
              </ul>
            </div>
          );
        })}
      </Card>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Reports & QBR page
// ─────────────────────────────────────────────────────────────────────

function ReportsPage() {
  const [metrics, setMetrics] = useState<Record<string, unknown> | null>(null);
  const [summary, setSummary] = useState<string>("");
  const [custId, setCustId] = useState("");
  const [qbr, setQbr] = useState<string>("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void fetch("/api/reporting/exec-summary", { credentials: "include" })
      .then((r) => r.json()).then((d: { summary: string; metrics: Record<string, unknown> }) => { setSummary(d.summary); setMetrics(d.metrics); });
  }, []);

  async function generateQbr() {
    const id = Number.parseInt(custId, 10);
    if (!Number.isFinite(id)) return;
    setBusy(true); setQbr("");
    try {
      const d = await fetch(`/api/reporting/qbr/${id}`, { credentials: "include" }).then((r) => r.json()) as { markdown?: string; error?: string };
      setQbr(d.markdown ?? d.error ?? "No data.");
    } finally { setBusy(false); }
  }

  const m = metrics as { tickets_total?: number; auto_route_rate?: number; ai_cost_gbp?: string; qa?: { pass_rate?: number }; top_skills?: Array<{ skill: string; n: number }> } | null;

  return (
    <>
      <PageHeader eyebrow="Intelligence" title="Reports & QBR" subtitle="Tenant-wide rollup + auto-assembled quarterly business reviews from first-party ticket, QA, and sentiment data." />
      {m && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "1rem", marginBottom: "1.5rem" }}>
          <Stat label="Tickets" value={String(m.tickets_total ?? 0)} />
          <Stat label="Auto-route rate" value={`${Math.round((m.auto_route_rate ?? 0) * 100)}%`} />
          <Stat label="QA pass" value={`${Math.round((m.qa?.pass_rate ?? 0) * 100)}%`} />
          <Stat label="AI spend" value={`£${m.ai_cost_gbp ?? "0.00"}`} />
        </div>
      )}
      <Card title="Executive summary" subtitle="AI-narrated when the Anthropic key is set; otherwise a precise templated rollup.">
        <p style={{ color: "var(--ink-1)", lineHeight: 1.6 }}>{summary || "Loading…"}</p>
        {m?.top_skills && m.top_skills.length > 0 && (
          <div style={{ marginTop: "1rem", display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
            {m.top_skills.slice(0, 8).map((s) => (
              <span key={s.skill} className="mono" style={{ fontSize: "0.72rem", border: "1px solid var(--line-2)", borderRadius: 2, padding: "0.15rem 0.45rem", color: "var(--ink-2)" }}>{s.skill} · {s.n}</span>
            ))}
          </div>
        )}
      </Card>
      <Card title="Generate a QBR deck" subtitle="Enter a HaloPSA customer ID. Produces a full markdown deck you can paste into a doc or send.">
        <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1rem" }}>
          <input value={custId} onChange={(e) => setCustId(e.target.value)} placeholder="Customer ID (e.g. 42)" style={{ ...inputStyle, maxWidth: 220 }} />
          <button onClick={generateQbr} disabled={busy || !custId} className="primary-button">{busy ? "Generating…" : "Generate QBR →"}</button>
        </div>
        {qbr && (
          <pre style={{ fontFamily: "var(--font-mono)", fontSize: "0.78rem", color: "var(--ink-1)", whiteSpace: "pre-wrap", background: "var(--bg-2)", padding: "1rem", borderRadius: "var(--r-sm)", margin: 0, maxHeight: 480, overflow: "auto", lineHeight: 1.5 }}>{qbr}</pre>
        )}
      </Card>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Team (technician roster + dispatch) page
// ─────────────────────────────────────────────────────────────────────

interface TechRow {
  id: string; name: string; halo_agent_id: number | null;
  skills: string; specializations: string; seniority: number; max_concurrent: number; available: boolean;
}

function TeamPage() {
  const [techs, setTechs] = useState<TechRow[]>([]);
  const [name, setName] = useState("");
  const [skills, setSkills] = useState("");
  const [seniority, setSeniority] = useState("3");
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    const d = await fetch("/api/technicians", { credentials: "include" }).then((r) => r.json()) as { technicians: TechRow[] };
    setTechs(d.technicians);
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  async function add() {
    setBusy(true);
    try {
      await fetch("/api/technicians", {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          skills: skills.split(",").map((s) => s.trim()).filter(Boolean),
          seniority: Number.parseInt(seniority, 10),
        }),
      });
      setName(""); setSkills(""); void reload();
    } finally { setBusy(false); }
  }
  async function remove(id: string) {
    await fetch(`/api/technicians/${id}`, { method: "DELETE", credentials: "include" });
    void reload();
  }

  return (
    <>
      <PageHeader eyebrow="Operations" title="Team & dispatch" subtitle="Technician skill matrix. Alan uses this to SUGGEST the right tech per ticket — it never auto-assigns. Assignment stays a human decision." />
      <Card title="Add technician">
        <div style={{ display: "grid", gridTemplateColumns: "1.2fr 2fr 0.6fr auto", gap: "0.5rem", alignItems: "end" }}>
          <label><span className="eyebrow" style={{ display: "block", marginBottom: "0.3rem" }}>Name</span><input value={name} onChange={(e) => setName(e.target.value)} style={inputStyle} /></label>
          <label><span className="eyebrow" style={{ display: "block", marginBottom: "0.3rem" }}>Skills (comma-separated)</span><input value={skills} onChange={(e) => setSkills(e.target.value)} placeholder="password-reset, vpn-setup, m365" style={inputStyle} /></label>
          <label><span className="eyebrow" style={{ display: "block", marginBottom: "0.3rem" }}>Seniority</span><select value={seniority} onChange={(e) => setSeniority(e.target.value)} style={inputStyle}>{[1,2,3,4,5].map((n) => <option key={n} value={n}>{n}</option>)}</select></label>
          <button onClick={add} disabled={busy || name.length < 2} className="primary-button">Add</button>
        </div>
      </Card>
      <Card title={`Roster (${techs.length})`}>
        {techs.length === 0 && <p style={{ color: "var(--ink-2)" }}>No technicians yet. Add your team above so Alan can suggest assignees.</p>}
        {techs.map((t) => (
          <div key={t.id} style={{ display: "flex", alignItems: "center", gap: "1rem", padding: "0.65rem 0", borderTop: "1px solid var(--line)" }}>
            <span style={{ color: "var(--ink-0)", fontWeight: 500, width: 160 }}>{t.name}</span>
            <span style={{ flex: 1, display: "flex", gap: "0.3rem", flexWrap: "wrap" }}>
              {(JSON.parse(t.skills || "[]") as string[]).map((s) => <span key={s} className="mono" style={{ fontSize: "0.68rem", border: "1px solid var(--line-2)", borderRadius: 2, padding: "0.1rem 0.4rem", color: "var(--ink-2)" }}>{s}</span>)}
            </span>
            <span className="mono" style={{ fontSize: "0.72rem", color: "var(--ink-2)" }}>L{t.seniority}</span>
            <span style={{ fontSize: "0.72rem", color: t.available ? "var(--ok)" : "var(--ink-3)" }}>{t.available ? "available" : "off"}</span>
            <button onClick={() => remove(t.id)} className="ghost-button" style={{ padding: "0.25rem 0.6rem", fontSize: "0.72rem" }}>Remove</button>
          </div>
        ))}
      </Card>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Alerts page
// ─────────────────────────────────────────────────────────────────────

interface AlertRow {
  id: string; kind: string; severity: string; title: string; body: string;
  status: string; created_at: number;
}

const SEVERITY_COLOUR: Record<string, string> = {
  info: "var(--info)", warning: "var(--warn)", critical: "var(--danger)",
};

function AlertsPage() {
  const [alerts, setAlerts] = useState<AlertRow[]>([]);
  const [busy, setBusy] = useState(false);
  const reload = useCallback(async () => {
    const d = await fetch("/api/alerts?status=open", { credentials: "include" }).then((r) => r.json()) as { alerts: AlertRow[] };
    setAlerts(d.alerts);
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  async function ack(id: string) {
    await fetch(`/api/alerts/${id}/ack`, { method: "POST", credentials: "include" });
    void reload();
  }
  async function runDetectors() {
    setBusy(true);
    try {
      await fetch("/api/alerts/run-detectors", { method: "POST", credentials: "include" });
      void reload();
    } finally { setBusy(false); }
  }

  return (
    <>
      <PageHeader
        eyebrow="Operations"
        title="Proactive alerts"
        subtitle="Stale tickets, predicted SLA breaches, worsening sentiment, integration failures. Detectors run daily; trigger a sweep on demand."
        action={<button onClick={runDetectors} disabled={busy} className="primary-button">{busy ? "Running…" : "Run detectors now"}</button>}
      />
      <Card title={`Open alerts (${alerts.length})`}>
        {alerts.length === 0 && <p style={{ color: "var(--ink-2)" }}>No open alerts. Everything's quiet.</p>}
        {alerts.map((a) => (
          <div key={a.id} style={{ display: "flex", gap: "1rem", padding: "0.85rem 0", borderTop: "1px solid var(--line)", alignItems: "flex-start" }}>
            <span style={{ width: 8, height: 8, borderRadius: 999, background: SEVERITY_COLOUR[a.severity] ?? "var(--ink-2)", marginTop: 6 }} />
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", gap: "0.5rem", alignItems: "baseline" }}>
                <span style={{ color: "var(--ink-0)", fontWeight: 600 }}>{a.title}</span>
                <span className="mono" style={{ fontSize: "0.68rem", color: "var(--ink-2)" }}>{a.kind}</span>
              </div>
              <div style={{ color: "var(--ink-1)", fontSize: "0.85rem", marginTop: "0.2rem" }}>{a.body}</div>
            </div>
            <button onClick={() => ack(a.id)} className="ghost-button" style={{ padding: "0.3rem 0.7rem", fontSize: "0.75rem" }}>Acknowledge</button>
          </div>
        ))}
      </Card>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Audit page
// ─────────────────────────────────────────────────────────────────────

interface AuditRow {
  id: string;
  ts: number;
  actor_type: string;
  actor_id: string | null;
  action: string;
  target_type: string | null;
  target_id: string | null;
  flagged_for_review?: boolean;
  review_job_id?: string | null;
}

interface VerifyResult {
  total: number;
  valid: boolean;
  invalid: Array<{ id: string; reason: string }>;
  checked_at: number;
}

type AuditActorFilter = "all" | "user" | "alan" | "system";

function Audit() {
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [actor, setActor] = useState<AuditActorFilter>("all");
  const [flagged, setFlagged] = useState(false);
  const [q, setQ] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState<VerifyResult | null>(null);

  const reload = useCallback(async () => {
    const qs = new URLSearchParams();
    if (actor !== "all") qs.set("actor", actor);
    if (flagged) qs.set("flagged", "1");
    if (q.trim()) qs.set("q", q.trim());
    const url = `/api/settings/audit${qs.toString() ? `?${qs}` : ""}`;
    const r = await fetch(url, { credentials: "include" }).then((r) => r.json()) as { entries: AuditRow[] };
    setRows(r.entries);
  }, [actor, flagged, q]);
  useEffect(() => { void reload(); }, [reload]);

  async function verify() {
    setVerifying(true);
    try {
      const r = await fetch("/api/settings/audit/verify", { credentials: "include" }).then((r) => r.json()) as VerifyResult;
      setVerifyResult(r);
    } finally {
      setVerifying(false);
    }
  }

  return (
    <>
      <PageHeader
        eyebrow="Audit log"
        title="Every action, hash-chained"
        subtitle="Append-only. Each row's hash includes the prior row's. Tamper one row and the chain breaks at that index — Verify chain proves it."
        action={
          <button onClick={verify} disabled={verifying} className="primary-button">
            {verifying ? "Verifying…" : "Verify chain →"}
          </button>
        }
      />

      {verifyResult && (
        <div style={{
          marginBottom: "1.25rem",
          padding: "0.85rem 1.1rem",
          borderRadius: "var(--r-md)",
          border: `1px solid ${verifyResult.valid ? "var(--ok)" : "var(--danger)"}`,
          background: verifyResult.valid ? "var(--ok-soft)" : "var(--danger-soft)",
          display: "flex",
          alignItems: "center",
          gap: "1rem",
          fontSize: "0.88rem",
        }}>
          <span style={{ width: 8, height: 8, borderRadius: 999, background: verifyResult.valid ? "var(--ok)" : "var(--danger)" }} />
          <span style={{ flex: 1, color: "var(--ink-0)" }}>
            <strong style={{ color: verifyResult.valid ? "var(--ok)" : "var(--danger)" }}>
              {verifyResult.valid ? "Chain intact." : `Chain broken at ${verifyResult.invalid.length} row(s).`}
            </strong>{" "}
            Checked {verifyResult.total} row{verifyResult.total === 1 ? "" : "s"} at {new Date(verifyResult.checked_at).toLocaleString("en-GB")}.
          </span>
          {!verifyResult.valid && (
            <details style={{ fontFamily: "var(--font-mono)", fontSize: "0.78rem", color: "var(--danger)" }}>
              <summary style={{ cursor: "pointer" }}>show ids</summary>
              <pre style={{ margin: "0.5rem 0 0", whiteSpace: "pre-wrap" }}>{verifyResult.invalid.map((i) => `${i.id}: ${i.reason}`).join("\n")}</pre>
            </details>
          )}
        </div>
      )}

      <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1.25rem", flexWrap: "wrap", alignItems: "center" }}>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search action, target, actor…"
          style={{ ...inputStyle, maxWidth: 360, flex: "1 1 240px" }}
        />
        {(["all", "user", "alan", "system"] as AuditActorFilter[]).map((a) => (
          <button
            key={a}
            onClick={() => setActor(a)}
            className={actor === a ? "primary-button" : "ghost-button"}
            style={{ padding: "0.5rem 0.85rem", fontSize: "0.82rem" }}
          >
            {a}
          </button>
        ))}
        <button
          onClick={() => setFlagged(!flagged)}
          className={flagged ? "primary-button" : "ghost-button"}
          style={{ padding: "0.5rem 0.85rem", fontSize: "0.82rem", borderColor: flagged ? "var(--warn)" : undefined, background: flagged ? "var(--warn)" : undefined, color: flagged ? "var(--accent-ink)" : undefined }}
        >
          {flagged ? "✓ Flagged only" : "Flagged only"}
        </button>
      </div>

      <div style={{ border: "1px solid var(--line)", borderRadius: "var(--r-md)", overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "var(--font-mono)", fontSize: "0.85rem" }}>
          <thead style={{ background: "var(--bg-2)", color: "var(--ink-2)" }}>
            <tr>
              <th style={{ ...th, width: 170 }}>Timestamp</th>
              <th style={th}>Actor</th>
              <th style={th}>Action</th>
              <th style={th}>Target</th>
              <th style={{ ...th, width: 90 }}>Flag</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={5} style={{ ...td, color: "var(--ink-2)", textAlign: "center", padding: "2rem" }}>No entries match these filters.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} style={{ borderTop: "1px solid var(--line)", background: r.flagged_for_review ? "var(--warn-soft)" : undefined }}>
                <td style={td}>{new Date(r.ts).toLocaleString("en-GB")}</td>
                <td style={td}><span style={{ color: r.actor_type === "alan" ? "var(--accent)" : r.actor_type === "user" ? "var(--info)" : "var(--ink-2)" }}>{r.actor_type}</span></td>
                <td style={td}>{r.action}</td>
                <td style={{ ...td, color: "var(--ink-2)" }}>{r.target_type && r.target_id ? `${r.target_type}:${r.target_id.slice(0, 16)}…` : "—"}</td>
                <td style={td}>{r.flagged_for_review ? <span style={{ color: "var(--warn)", fontWeight: 600 }}>review</span> : <span style={{ color: "var(--ink-3)" }}>—</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

const th: React.CSSProperties = { padding: "0.6rem 1rem", textAlign: "left", fontWeight: 500, letterSpacing: "0.04em", fontSize: "0.75rem", textTransform: "uppercase" };
const td: React.CSSProperties = { padding: "0.7rem 1rem", color: "var(--ink-1)" };

// ─────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────

const inputStyle: React.CSSProperties = { width: "100%", padding: "0.7rem 0.85rem", background: "var(--bg-2)", border: "1px solid var(--line-2)", borderRadius: "var(--r-sm)", color: "var(--ink-0)", fontSize: "0.95rem", outline: "none", fontFamily: "inherit" };

function FullPageLoader({ label }: { label: string }) {
  return <main style={{ display: "grid", placeItems: "center", minHeight: "100vh" }}><p className="eyebrow">{label}</p></main>;
}

function FullPageError({ message }: { message: string }) {
  return <main style={{ display: "grid", placeItems: "center", minHeight: "100vh", padding: "2rem" }}><div><p className="eyebrow" style={{ color: "var(--danger)" }}>Error</p><p style={{ color: "var(--ink-1)", marginTop: "0.5rem" }}>{message}</p></div></main>;
}

function NotFound() {
  return (
    <main style={{ padding: "5rem 2rem", textAlign: "center" }}>
      <p className="eyebrow">404</p>
      <h1 style={{ fontFamily: "var(--font-display)", fontSize: "2.25rem", margin: "0.75rem 0 1rem" }}>Not here.</h1>
      <Link to="/" style={{ color: "var(--accent)" }}>← back to the landing</Link>
    </main>
  );
}

// ─────────────────────────────────────────────────────────────────────
// NOC — live operations view (polling-based, 3s)
// ─────────────────────────────────────────────────────────────────────

interface NocSnapshot {
  server_ts: number;
  tenant: { id: string; name: string; dry_run: boolean; killswitch: boolean; plan: string; seats_used: number; seats_licensed: number };
  mode: "killswitch" | "dry-run" | "live";
  jobs: Record<string, number> & { total: number; in_flight: number };
  tickets: Record<string, number> & { total: number };
  integrations: Record<string, { provider: string; status: string; last_error: string | null } | null>;
  skills: { total_global: number; enabled: number; jobs_last_hour: number };
  audit: Array<{ id: string; ts: number; actor_type: string; actor_id: string | null; action: string; target_type: string | null; target_id: string | null }>;
}

function NOC() {
  const [snap, setSnap] = useState<NocSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    async function fetchOnce() {
      try {
        const r = await fetch("/api/noc/snapshot", { credentials: "include" });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = (await r.json()) as NocSnapshot;
        if (!cancelled) setSnap(data);
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      } finally {
        if (!cancelled && !paused) timer = setTimeout(fetchOnce, 3000);
      }
    }
    void fetchOnce();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [paused]);

  if (!snap && !error) return <FullPageLoader label="Connecting to live operations…" />;
  if (error) return <FullPageError message={error} />;
  if (!snap) return null;

  return (
    <>
      <PageHeader
        eyebrow="Live operations"
        title="What Alan is doing right now"
        subtitle={`Snapshot age: ${Math.max(0, Math.floor((Date.now() - snap.server_ts) / 1000))}s · auto-refresh every 3s`}
        action={
          <button onClick={() => setPaused((p) => !p)} className="ghost-button">
            {paused ? "▶ Resume" : "⏸ Pause"}
          </button>
        }
      />

      {/* Mode banner */}
      <ModeBanner snap={snap} />

      {/* Top-level stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "0.75rem", marginBottom: "1.5rem" }}>
        <Stat label="Jobs in flight" value={String(snap.jobs.in_flight)} sub={`${snap.jobs.total} total`} accent={snap.jobs.in_flight > 0 ? "var(--accent)" : undefined} />
        <Stat label="Awaiting approval" value={String(snap.jobs["awaiting_approval"] ?? 0)} sub="Pending engineer review" accent={(snap.jobs["awaiting_approval"] ?? 0) > 0 ? "var(--warn)" : undefined} />
        <Stat label="Tickets tracked" value={String(snap.tickets.total)} sub={`${snap.tickets["processing"] ?? 0} processing`} />
        <Stat label="Skills enabled" value={`${snap.skills.enabled} / ${snap.skills.total_global}`} sub={`${snap.skills.jobs_last_hour} jobs in last hour`} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 360px", gap: "1.25rem", alignItems: "start" }}>
        {/* Left column */}
        <div>
          <Card title="Ticket state breakdown" subtitle="Counts across HaloPSA workflow statuses for this tenant.">
            <TicketStateGrid tickets={snap.tickets} />
          </Card>
          <Card title="Job queue" subtitle="Tasks the orchestrator is tracking.">
            <JobStateGrid jobs={snap.jobs} />
          </Card>
          <Card title="Integration health" subtitle="Connection state for each external system.">
            <IntegrationHealth integrations={snap.integrations} />
          </Card>
        </div>

        {/* Right column — live audit feed */}
        <Card title="Live activity feed" subtitle="Last 50 audit entries · hash-chained">
          <LiveAuditFeed entries={snap.audit} />
        </Card>
      </div>
    </>
  );
}

function ModeBanner({ snap }: { snap: NocSnapshot }) {
  if (snap.tenant.killswitch) {
    return (
      <div style={{ padding: "1rem 1.25rem", marginBottom: "1.5rem", border: "1px solid var(--danger)", background: "var(--danger-soft)", borderRadius: "var(--r-md)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ color: "var(--danger)", fontFamily: "var(--font-display)", fontSize: "1.1rem", fontWeight: 700 }}>Kill switch active</div>
          <div style={{ color: "var(--ink-1)", fontSize: "0.88rem", marginTop: "0.15rem" }}>All polling and execution halted. Re-enable in Settings.</div>
        </div>
        <Link to="/app/settings" className="ghost-button">Settings →</Link>
      </div>
    );
  }
  if (snap.tenant.dry_run) {
    return (
      <div style={{ padding: "1rem 1.25rem", marginBottom: "1.5rem", border: "1px solid var(--warn)", background: "var(--warn-soft)", borderRadius: "var(--r-md)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ color: "var(--warn)", fontFamily: "var(--font-display)", fontSize: "1.05rem", fontWeight: 700 }}>Dry-run mode</div>
          <div style={{ color: "var(--ink-1)", fontSize: "0.88rem", marginTop: "0.15rem" }}>Alan reads + drafts but never writes to your Halo. Flip off when you're ready.</div>
        </div>
        <Link to="/app/settings" className="ghost-button">Settings →</Link>
      </div>
    );
  }
  return null;
}

function TicketStateGrid({ tickets }: { tickets: NocSnapshot["tickets"] }) {
  const states = [
    { key: "idle", label: "Idle", color: "var(--ink-2)" },
    { key: "processing", label: "Processing", color: "var(--info)" },
    { key: "proposal-ready", label: "Proposal ready", color: "var(--violet)" },
    { key: "email-draft", label: "Email draft", color: "var(--warn)" },
    { key: "blocked", label: "Blocked", color: "var(--danger)" },
    { key: "scheduled", label: "Scheduled", color: "var(--ink-2)" },
    { key: "done", label: "Done", color: "var(--ok)" },
  ];
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: "0.5rem" }}>
      {states.map((s) => {
        const n = tickets[s.key] ?? 0;
        return (
          <div key={s.key} style={{ padding: "0.7rem 0.85rem", border: "1px solid var(--line)", borderRadius: "var(--r-sm)", background: "var(--bg-2)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", marginBottom: "0.3rem" }}>
              <span style={{ width: 6, height: 6, borderRadius: 1, background: s.color }} />
              <span style={{ fontSize: "0.72rem", color: "var(--ink-2)", textTransform: "uppercase", letterSpacing: "0.04em" }}>{s.label}</span>
            </div>
            <div style={{ fontFamily: "var(--font-display)", fontSize: "1.4rem", fontWeight: 700, color: n > 0 ? "var(--ink-0)" : "var(--ink-3)" }}>{n}</div>
          </div>
        );
      })}
    </div>
  );
}

function JobStateGrid({ jobs }: { jobs: NocSnapshot["jobs"] }) {
  const states = [
    { key: "queued", label: "Queued", color: "var(--ink-2)" },
    { key: "running", label: "Running", color: "var(--info)" },
    { key: "awaiting_approval", label: "Awaiting approval", color: "var(--violet)" },
    { key: "stage_paused", label: "Stage paused", color: "var(--warn)" },
    { key: "completed", label: "Completed", color: "var(--ok)" },
    { key: "failed", label: "Failed", color: "var(--danger)" },
    { key: "cancelled", label: "Cancelled", color: "var(--ink-3)" },
  ];
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "0.5rem" }}>
      {states.map((s) => {
        const n = jobs[s.key] ?? 0;
        return (
          <div key={s.key} style={{ padding: "0.6rem 0.85rem", border: "1px solid var(--line)", borderRadius: "var(--r-sm)", background: "var(--bg-2)", display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <div>
              <div style={{ fontSize: "0.72rem", color: "var(--ink-2)", textTransform: "uppercase", letterSpacing: "0.04em" }}>{s.label}</div>
            </div>
            <div style={{ fontFamily: "var(--font-display)", fontSize: "1.25rem", fontWeight: 700, color: n > 0 ? s.color : "var(--ink-3)" }}>{n}</div>
          </div>
        );
      })}
    </div>
  );
}

function IntegrationHealth({ integrations }: { integrations: NocSnapshot["integrations"] }) {
  const items = [
    { key: "halopsa", label: "HaloPSA" },
    { key: "graph", label: "Microsoft 365" },
    { key: "ninja", label: "NinjaRMM" },
    { key: "itglue", label: "IT Glue" },
  ];
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "0.5rem" }}>
      {items.map((i) => {
        const live = integrations[i.key];
        const status = live?.status ?? "not-connected";
        return (
          <div key={i.key} style={{ padding: "0.85rem 1rem", border: "1px solid var(--line)", borderRadius: "var(--r-sm)", background: "var(--bg-2)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.35rem" }}>
              <span style={{ color: "var(--ink-0)", fontWeight: 500 }}>{i.label}</span>
              <StatusPill status={status} />
            </div>
            {live?.last_error && <div style={{ color: "var(--danger)", fontSize: "0.78rem", fontFamily: "var(--font-mono)" }}>{live.last_error.slice(0, 60)}</div>}
          </div>
        );
      })}
    </div>
  );
}

function LiveAuditFeed({ entries }: { entries: NocSnapshot["audit"] }) {
  if (entries.length === 0) {
    return <p style={{ color: "var(--ink-2)", fontSize: "0.88rem", textAlign: "center", padding: "2rem 1rem" }}>No activity yet. Audit entries appear here as Alan and your team work.</p>;
  }
  return (
    <div style={{ maxHeight: "560px", overflowY: "auto", margin: "-0.5rem -0.5rem", padding: "0.5rem" }}>
      {entries.map((e) => {
        const verb = e.action.split(".").pop() ?? e.action;
        const noun = e.action.split(".").slice(0, -1).join(".");
        const ts = new Date(e.ts);
        const actorColor = e.actor_type === "alan" ? "var(--accent)" : e.actor_type === "user" ? "var(--info)" : "var(--ink-2)";
        return (
          <div key={e.id} style={{ padding: "0.55rem 0.65rem", borderBottom: "1px solid var(--line)", fontSize: "0.83rem", fontFamily: "var(--font-mono)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.2rem" }}>
              <span style={{ color: actorColor, fontWeight: 600 }}>{e.actor_type}</span>
              <span style={{ color: "var(--ink-3)", fontSize: "0.72rem" }}>{ts.toLocaleTimeString("en-GB")}</span>
            </div>
            <div style={{ color: "var(--ink-1)", lineHeight: 1.4 }}>
              {noun && <span style={{ color: "var(--ink-2)" }}>{noun}.</span>}
              <span style={{ color: "var(--ink-0)" }}>{verb}</span>
              {e.target_type && e.target_id && (
                <span style={{ color: "var(--ink-3)", marginLeft: "0.5rem" }}>· {e.target_type}:{e.target_id.slice(0, 8)}…</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
